import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  increment,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, auth, db } from '../config/firebase';
import { sheetFormService } from './sheetFormService';
import { sanitizeHeaderConditionalLogic } from '../utils/sheetConditionalLogic';
import { parseFormulaToConfig, resolveRowFormulas, sanitizeHeaderConditionalFormulaRules } from '../utils/sheetFormula';
import { normalizeSheetOptionSource, sanitizeHeaderConditionalOptions } from '../utils/sheetOptions';
import { sheetWorkspaceService } from './sheetWorkspaceService';
import { userService } from './userService';
import { sanitizeAnalytics } from '../utils/sheetAnalytics';

const SHEETS_COLLECTION = 'docs-sheets';
const SHEET_FORMS_COLLECTION = 'docs-sheet-forms';
const SHEET_ITEMS_SUBCOLLECTION = 'sheet-items';
const SHARED_ROW_USERS_SUBCOLLECTION = 'shared-row-users';
const SHARED_ROW_ITEMS_SUBCOLLECTION = 'items';
const SHEET_SHARE_USERS_SUBCOLLECTION = 'share-users';
const SHEET_SHARE_EMAILS_SUBCOLLECTION = 'share-emails';
const SHEET_COLLABORATOR_ACCESS_VALUES = new Set(['view', 'edit_own_rows', 'view_all_edit_own', 'edit']);
const SHEET_USER_SHARES_COLLECTION = 'docs-sheet-user-shares';
const SHEET_LOGS_SUBCOLLECTION = 'activity-logs';
const SHEET_AI_THREADS_SUBCOLLECTION = 'ai-threads';
const SHEET_AI_MESSAGES_SUBCOLLECTION = 'messages';
const WORKSPACE_AI_CHAT_USERS_SUBCOLLECTION = 'ai-chat-users';
const WORKSPACE_AI_CHATS_SUBCOLLECTION = 'chats';
const SHEET_LOG_SCOPE_TYPES = new Set(['sheet', 'column', 'row']);
const SHEET_LOG_ACTIONS = new Set(['Call', 'Message', 'Email']);
const SHEET_LOG_STATUSES = new Set(['R', 'O', 'G']);
const functions = getFunctions(app);

const normalizeSheetTitle = (value = '') => `${value || ''}`.trim();
const getSheetTitleKey = (value = '') => normalizeSheetTitle(value).toLowerCase();

const normalizeEmails = (emails = []) => {
  if (!Array.isArray(emails)) return [];
  const cleaned = emails
    .map((email) => `${email || ''}`.trim().toLowerCase())
    .filter(Boolean);
  return Array.from(new Set(cleaned));
};

const sanitizeVisibility = (visibility) => {
  if (visibility === 'public' || visibility === 'restricted' || visibility === 'private') return visibility;
  return 'private';
};

const sanitizeCollaboratorAccess = (value) => {
  if (SHEET_COLLABORATOR_ACCESS_VALUES.has(value)) return value;
  return 'view';
};

const sanitizeColumnAccess = (value) => {
  if (value === 'hidden' || value === 'private' || value === 'restricted' || value === 'public') return value;
  return 'public';
};

const sanitizeTemplateFlag = (value) => value === true;
const sanitizeLogSheetFlag = (value) => value === true;
const sanitizeAdminBuilderAccessEnabled = (value) => value === true;
const ACTION_FORM_TYPES = new Set(['booking', 'checkout', 'inquiry', 'custom']);
const ACTION_CONFIG_MODES = new Set(['linked_form', 'update_rows']);
const ACTION_LINKED_FORM_BEHAVIORS = new Set(['open_form', 'instant_save']);
const ACTION_UPDATE_TARGET_CURRENT_SHEET = '__current_sheet__';
const ACTION_UPDATE_CONDITION_OPERATORS = new Set(['equals', 'notEquals', 'contains', 'isEmpty', 'isNotEmpty']);
const ACTION_UPDATE_VALUE_MODES = new Set(['static', 'sourceHeader']);
const SHEET_RENDER_PRESET_VALUES = new Set(['default', 'profiles', 'list-card', 'kanban', 'calendar']);
const sanitizeSheetRenderPreset = (value) => (
  SHEET_RENDER_PRESET_VALUES.has(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'default'
);
const SHEET_RENDER_FONT_VALUES = new Set([
  'Inter',
  'Roboto',
  'Open Sans',
  'Montserrat',
  'Poppins',
  'Lato',
  'Nunito',
  'Source Sans Pro',
  'Raleway',
  'Ubuntu',
  'Playfair Display',
  'Merriweather',
  'Georgia',
  'Times New Roman',
  'Crimson Text'
]);
const normalizeSheetRenderFont = (value) => (
  SHEET_RENDER_FONT_VALUES.has(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'Inter'
);
const normalizePresetUiColumns = (value, fallback = 3) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(1, Math.min(4, Math.round(numeric)));
};
const normalizeListCardImageLayout = (value) => (
  `${value || ''}`.trim() === 'full-width' ? 'full-width' : 'inline'
);
const LOG_SYSTEM_FIELDS = new Set([
  'logScope',
  'sourceSheetId',
  'sourceSheetTitle',
  'sourceRowId',
  'sourceColumnId',
  'sourceRowLabel',
  'sourceColumnLabel',
  'loggedAt',
  'loggedBy',
  'loggedByEmail'
]);

const normalizeUserIds = (userIds = []) => Array.from(new Set(
  (Array.isArray(userIds) ? userIds : [])
    .map((userId) => `${userId || ''}`.trim())
    .filter(Boolean)
));

const normalizeWorkspaceIds = (workspaceIds = []) => Array.from(new Set(
  (Array.isArray(workspaceIds) ? workspaceIds : [])
    .map((workspaceId) => `${workspaceId || ''}`.trim())
    .filter(Boolean)
));
const normalizeOrganizationIds = (organizationIds = []) => Array.from(new Set(
  (Array.isArray(organizationIds) ? organizationIds : [])
    .map((organizationId) => `${organizationId || ''}`.trim())
    .filter(Boolean)
));
const normalizeSheetThemeColor = (value) => {
  const normalized = `${value || ''}`.trim();
  if (!normalized) return undefined;
  const compact = normalized.startsWith('#') ? normalized.slice(1) : normalized;
  return /^(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(compact) ? `#${compact}` : undefined;
};
const normalizeSheetOgImageUrl = (value) => {
  const normalized = `${value || ''}`.trim();
  return normalized || undefined;
};

const isPermissionDeniedError = (error) => (
  error?.code === 'permission-denied' ||
  `${error?.message || ''}`.toLowerCase().includes('missing or insufficient permissions')
);

const stripUndefinedDeep = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }
  if (value && typeof value === 'object') {
    if (value.constructor !== Object) return value;
    const output = {};
    Object.entries(value).forEach(([key, item]) => {
      const cleaned = stripUndefinedDeep(item);
      if (cleaned !== undefined) output[key] = cleaned;
    });
    return output;
  }
  return value === undefined ? undefined : value;
};

const commitRowsInBatches = async (targetSheetId, rows = [], createdBy = null, createdByEmail = null) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const chunkSize = 400;

  for (let index = 0; index < safeRows.length; index += chunkSize) {
    const batch = writeBatch(db);
    const chunk = safeRows.slice(index, index + chunkSize);

    chunk.forEach((row) => {
      const rowRef = doc(collection(db, SHEETS_COLLECTION, targetSheetId, SHEET_ITEMS_SUBCOLLECTION));
      batch.set(rowRef, stripUndefinedDeep({
        sheetId: targetSheetId,
        values: row?.values || {},
        createdBy,
        createdByEmail,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }));
    });

    await batch.commit();
  }
};

const cloneLinkedSheetForm = async ({
  sourceSheetId,
  clonedSheet,
  clonedHeaders,
  ownerId,
  workspaceId
}) => {
  const sourceFormSnap = await getDoc(doc(db, SHEET_FORMS_COLLECTION, sourceSheetId));
  if (!sourceFormSnap.exists()) return;

  const sourceForm = sourceFormSnap.data() || {};
  const clonedHeadersById = new Map(
    (Array.isArray(clonedHeaders) ? clonedHeaders : [])
      .map((header) => [`${header?.id || ''}`.trim(), header])
      .filter(([headerId]) => Boolean(headerId))
  );

  const cloneFormFields = (fields = []) => (
    (Array.isArray(fields) ? fields : [])
      .map((field) => {
        const headerId = `${field?.id || ''}`.trim();
        const clonedHeader = clonedHeadersById.get(headerId);
        if (!clonedHeader) return null;
        return {
          ...clonedHeader,
          required: field?.required === true,
          formWidth: field?.formWidth || 'full'
        };
      })
      .filter(Boolean)
  );

  await setDoc(doc(db, SHEET_FORMS_COLLECTION, clonedSheet.id), stripUndefinedDeep({
    ...sourceForm,
    linkedSheetId: clonedSheet.id,
    linkedSheetTitle: clonedSheet.title || '',
    linkedSheetVisibility: clonedSheet.visibility || 'private',
    workspaceId: `${workspaceId || ''}`.trim() || undefined,
    userId: ownerId,
    accessMode: clonedSheet.visibility === 'public' ? 'public' : 'internal',
    sheetHeaders: clonedHeaders,
    fields: cloneFormFields(sourceForm.fields),
    repeatableFields: cloneFormFields(sourceForm.repeatableFields),
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp()
  }));
};

const remapWorkspaceCloneOptionSource = (optionSource, sheetIdMap = new Map(), workspaceSheetIds = new Set()) => {
  const normalizedSource = normalizeSheetOptionSource(optionSource);
  if (!normalizedSource?.sheetId || !workspaceSheetIds.has(normalizedSource.sheetId)) {
    return normalizedSource || undefined;
  }

  const nextSheetId = sheetIdMap.get(normalizedSource.sheetId);
  if (!nextSheetId) {
    return undefined;
  }

  return {
    ...normalizedSource,
    sheetId: nextSheetId
  };
};

const remapWorkspaceCloneHeaders = (headers = [], sheetIdMap = new Map(), workspaceSheetIds = new Set()) => (
  (Array.isArray(headers) ? headers : []).map((header) => {
    const optionSource = remapWorkspaceCloneOptionSource(header?.optionSource, sheetIdMap, workspaceSheetIds);
    const conditionalOptions = (Array.isArray(header?.conditionalOptions) ? header.conditionalOptions : [])
      .map((rule) => {
        const nextOptionSource = remapWorkspaceCloneOptionSource(rule?.optionSource, sheetIdMap, workspaceSheetIds);
        return stripUndefinedDeep({
          ...rule,
          optionSource: nextOptionSource
        });
      });

    return stripUndefinedDeep({
      ...header,
      optionSource,
      conditionalOptions: conditionalOptions.length ? conditionalOptions : undefined
    });
  })
);

const remapWorkspaceCloneActionForms = (actionForms = [], sheetIdMap = new Map(), workspaceSheetIds = new Set()) => (
  (Array.isArray(actionForms) ? actionForms : [])
    .map((actionForm) => {
      const sourceTargetSheetId = `${actionForm?.targetSheetId || ''}`.trim();
      if (!sourceTargetSheetId) return null;
      if (!workspaceSheetIds.has(sourceTargetSheetId)) {
        return actionForm;
      }

      const nextTargetSheetId = sheetIdMap.get(sourceTargetSheetId);
      if (!nextTargetSheetId) return null;

      return {
        ...actionForm,
        targetSheetId: nextTargetSheetId
      };
    })
    .filter(Boolean)
);

const remapSingleSheetCloneActionForms = (actionForms = [], sourceSheetId = '', clonedSheetId = '') => {
  const sourceId = `${sourceSheetId || ''}`.trim();
  const cloneId = `${clonedSheetId || ''}`.trim();
  if (!sourceId || !cloneId) return [];

  return (Array.isArray(actionForms) ? actionForms : [])
    .map((actionForm) => {
      const sourceTargetSheetId = `${actionForm?.targetSheetId || ''}`.trim();
      if (!sourceTargetSheetId) return null;

      return {
        ...actionForm,
        targetSheetId: sourceTargetSheetId === sourceId ? cloneId : sourceTargetSheetId
      };
    })
    .filter(Boolean);
};

const remapWorkspaceCloneLinkedForms = async ({
  clonedSheetId,
  sheetIdMap,
  workspaceSheetIds
}) => {
  const clonedForm = await sheetFormService.getSheetFormById(clonedSheetId);
  if (!clonedForm?.id) return;

  await updateDoc(doc(db, SHEET_FORMS_COLLECTION, clonedSheetId), stripUndefinedDeep({
    sheetHeaders: remapWorkspaceCloneHeaders(clonedForm.sheetHeaders || [], sheetIdMap, workspaceSheetIds),
    fields: remapWorkspaceCloneHeaders(clonedForm.fields || [], sheetIdMap, workspaceSheetIds),
    repeatableFields: remapWorkspaceCloneHeaders(clonedForm.repeatableFields || [], sheetIdMap, workspaceSheetIds),
    updatedAt: serverTimestamp()
  }));
};

const normalizeHeaderRenderColor = (value) => normalizeSheetThemeColor(value);

const withSheetWriteStep = async (label, operation) => {
  try {
    return await operation();
  } catch (error) {
    const nextError = error instanceof Error ? error : new Error(`${error}`);
    nextError.message = `${label} failed: ${nextError.message || 'Unknown error'}`;
    nextError.saveStep = nextError.saveStep || label;
    throw nextError;
  }
};

const sanitizeHeaders = (headers = []) => {
  if (!Array.isArray(headers)) return [];
  const seen = new Set();
  let hasAssignedRowLabel = false;
  const fixedHeaderOptions = {
    yesno: ['Yes', 'No'],
    rog: ['Red', 'Orange', 'Green']
  };
  const normalizeLegacyHeaderType = (value) => {
    const normalized = `${value || ''}`.trim();
    if (['barcode', 'qrcode', 'nfc'].includes(normalized)) return 'text';
    return normalized;
  };
  const getLegacyInputModeFromHeaderType = (value) => {
    const normalized = `${value || ''}`.trim();
    return ['barcode', 'qrcode', 'nfc'].includes(normalized) ? normalized : 'default';
  };
  const canUseSensorInputMode = (headerType) => (
    ['text', 'email', 'telephone', 'address', 'hyperlink'].includes(`${headerType || ''}`.trim())
  );
  const sanitizeConditionalDisplayNames = (rules = [], allHeaders = [], currentHeaderId = '') => {
    const validHeaderIds = new Set(
      (Array.isArray(allHeaders) ? allHeaders : [])
        .map((candidate) => `${candidate?.id || ''}`.trim())
        .filter((headerId) => headerId && headerId !== currentHeaderId)
    );
    const validOperators = new Set(['equals', 'notEquals', 'contains', 'notContains', 'greaterThan', 'lessThan', 'isEmpty', 'isNotEmpty']);

    return (Array.isArray(rules) ? rules : [])
      .map((rule) => {
        const whenHeaderId = `${rule?.whenHeaderId || rule?.headerId || rule?.fieldId || ''}`.trim();
        const displayName = `${rule?.displayName || rule?.label || rule?.displayLabel || ''}`.trim();
        if (!whenHeaderId || !validHeaderIds.has(whenHeaderId) || !displayName) return null;
        const operator = validOperators.has(`${rule?.operator || ''}`.trim()) ? `${rule.operator}`.trim() : 'equals';
        return {
          whenHeaderId,
          operator,
          value: `${rule?.value ?? ''}`,
          displayName
        };
      })
      .filter(Boolean);
  };
  const normalizeFormInputMode = (value) => (
    ['default', 'barcode', 'qrcode', 'nfc'].includes(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'default'
  );
  const allowedTypes = new Set([
    'text',
    'textarea',
    'number',
    'checkbox',
    'select',
    'buttons',
    'yesno',
    'rog',
    'multiselect',
    'rating',
    'percent',
    'email',
    'telephone',
    'address',
    'hyperlink',
    'youtube',
    'document',
    'image',
    'profile',
    'list',
    'date',
    'time',
    'datetime',
    'currency'
  ]);

  return headers
    .map((header, index) => {
      const id = `${header?.id || `column_${index + 1}`}`.trim();
      const label = `${header?.label || header?.name || ''}`.trim();
      const displayName = `${header?.displayName || header?.displayLabel || ''}`.trim();
      if (!id || !label || seen.has(id)) return null;
      seen.add(id);
      const width = Math.max(60, Number(header?.width) || 180);
      const type = allowedTypes.has(`${header?.type || 'text'}`.trim()) ? `${header?.type || 'text'}`.trim() : 'text';
      const systemField = LOG_SYSTEM_FIELDS.has(`${header?.systemField || ''}`.trim())
        ? `${header?.systemField}`.trim()
        : undefined;
      const columnAccess = sanitizeColumnAccess(header?.columnAccess);
      const textBold = Boolean(header?.textBold);
      const textItalic = Boolean(header?.textItalic);
      const textUnderline = Boolean(header?.textUnderline);
      const textAlign = ['left', 'center', 'right'].includes(`${header?.textAlign || ''}`.trim())
        ? `${header?.textAlign}`.trim()
        : 'left';
      const renderColor = normalizeHeaderRenderColor(header?.renderColor);
      const isRowLabel = Boolean(header?.isRowLabel) && !hasAssignedRowLabel;
      if (isRowLabel) {
        hasAssignedRowLabel = true;
      }
      const decimalDigits = (type === 'number' || type === 'currency' || type === 'percent')
        ? (() => {
            const numeric = Number(header?.decimalDigits);
            if (!Number.isFinite(numeric)) return 2;
            return Math.max(0, Math.min(6, Math.round(numeric)));
          })()
        : undefined;
      const allowedSummaryTypes = (type === 'number' || type === 'rating' || type === 'currency' || type === 'percent')
        ? new Set(['none', 'sum', 'average', 'min', 'max', 'count'])
        : new Set(['none', 'count']);
      const summaryType = allowedSummaryTypes.has(`${header?.summaryType || 'none'}`.trim())
        ? `${header?.summaryType || 'none'}`.trim()
        : 'none';
      const defaultValueMode = ['none', 'editable', 'fixed'].includes(`${header?.defaultValueMode || 'none'}`.trim())
        ? `${header?.defaultValueMode || 'none'}`.trim()
        : 'none';
      const options = (type === 'select' || type === 'buttons' || type === 'yesno' || type === 'rog' || type === 'multiselect')
        ? Array.from(new Set(
            ((fixedHeaderOptions[type] || (Array.isArray(header?.options) ? header.options : [])))
              .map((option) => `${option || ''}`.trim())
              .filter(Boolean)
          ))
        : [];
      const optionSource = (type === 'select' || type === 'buttons' || type === 'yesno' || type === 'rog' || type === 'multiselect')
        ? normalizeSheetOptionSource(header?.optionSource)
        : null;
      const conditionalOptions = (type === 'select' || type === 'buttons' || type === 'yesno' || type === 'rog' || type === 'multiselect')
        ? sanitizeHeaderConditionalOptions(header?.conditionalOptions, headers, id)
        : [];
      const validHeaderIds = new Set(
        (Array.isArray(headers) ? headers : [])
          .map((candidate) => `${candidate?.id || ''}`.trim())
          .filter((headerId) => headerId && headerId !== id)
      );
      const profileConfig = type === 'profile'
        ? {
            imageHeaderId: validHeaderIds.has(`${header?.profileConfig?.imageHeaderId || ''}`.trim()) ? `${header?.profileConfig?.imageHeaderId || ''}`.trim() : '',
            labelHeaderId: validHeaderIds.has(`${header?.profileConfig?.labelHeaderId || ''}`.trim()) ? `${header?.profileConfig?.labelHeaderId || ''}`.trim() : ''
          }
        : undefined;
      const formula = `${header?.formula || ''}`.trim();
      const optionsFromHeaderId = (type === 'select' || type === 'buttons' || type === 'yesno' || type === 'rog' || type === 'multiselect')
        ? (validHeaderIds.has(`${header?.optionsFromHeaderId || ''}`.trim()) ? `${header?.optionsFromHeaderId || ''}`.trim() : undefined)
        : undefined;
      const conditionalLogic = sanitizeHeaderConditionalLogic(header?.conditionalLogic, headers, id);
      const conditionalFormulaRules = sanitizeHeaderConditionalFormulaRules(header?.conditionalFormulaRules, headers, id);
      const conditionalDisplayNames = sanitizeConditionalDisplayNames(header?.conditionalDisplayNames || header?.conditionalDisplayLabels, headers, id);
      const formInputMode = canUseSensorInputMode(normalizeLegacyHeaderType(type))
        ? normalizeFormInputMode(header?.formInputMode || getLegacyInputModeFromHeaderType(header?.type))
        : 'default';
      const defaultValue = (() => {
        if (defaultValueMode === 'none') return undefined;
        if (type === 'checkbox') return Boolean(header?.defaultValue);
        if (type === 'multiselect' || type === 'list') {
          return Array.from(new Set(
            (Array.isArray(header?.defaultValue) ? header.defaultValue : `${header?.defaultValue || ''}`.split(','))
              .map((item) => `${item || ''}`.trim())
              .filter(Boolean)
          ));
        }
        if (type === 'number' || type === 'rating' || type === 'currency' || type === 'percent') {
          if (header?.defaultValue === '' || header?.defaultValue === null || header?.defaultValue === undefined) return undefined;
          const numeric = Number(header.defaultValue);
          return Number.isFinite(numeric) ? numeric : undefined;
        }
        return `${header?.defaultValue ?? ''}`;
      })();
      const baseValue = (type === 'number' || type === 'rating' || type === 'currency' || type === 'percent')
        ? (header?.baseValue === '' || header?.baseValue === null || header?.baseValue === undefined
            ? undefined
            : Number(header.baseValue))
        : undefined;
      const baseItems = (type === 'number' || type === 'rating' || type === 'currency' || type === 'percent')
        ? Array.from(new Set(
            (Array.isArray(header?.baseItems) ? header.baseItems : `${header?.baseItems || ''}`.split(','))
              .map((item) => `${item || ''}`.trim())
              .filter(Boolean)
          ))
        : [];
      return {
        id,
        label,
        displayName: displayName || undefined,
        type,
        width,
        isRowLabel,
        columnAccess,
        systemField,
        textBold,
        textItalic,
        textUnderline,
        textAlign,
        renderColor,
        rogOverlay: type === 'rog' ? Boolean(header?.rogOverlay) : undefined,
        decimalDigits,
        summaryType,
        defaultValueMode,
        defaultValue,
        options,
        optionsFromHeaderId,
        optionSource: optionSource || undefined,
        conditionalOptions: conditionalOptions.length ? conditionalOptions : undefined,
        profileConfig: profileConfig && (profileConfig.imageHeaderId || profileConfig.labelHeaderId) ? profileConfig : undefined,
        baseValue,
        baseItems,
        formula,
        formInputMode,
        conditionalLogic: conditionalLogic || undefined,
        conditionalFormulaRules: conditionalFormulaRules.length ? conditionalFormulaRules : undefined,
        conditionalDisplayNames: conditionalDisplayNames.length ? conditionalDisplayNames : undefined
      };
    })
    .filter(Boolean);
};

const sanitizeFilterHeaderIds = (filterHeaderIds = [], headers = []) => {
  if (!Array.isArray(filterHeaderIds)) return [];
  const validHeaderIds = new Set(sanitizeHeaders(headers).map((header) => header.id));
  return Array.from(new Set(
    filterHeaderIds
      .map((headerId) => `${headerId || ''}`.trim())
      .filter((headerId) => headerId && validHeaderIds.has(headerId))
  ));
};
const sanitizeActionForms = (actionForms = []) => {
  if (!Array.isArray(actionForms)) return [];

  const seen = new Set();
  return actionForms
    .map((actionForm, index) => {
      const id = `${actionForm?.id || `action_form_${index + 1}`}`.trim();
      const targetSheetId = `${actionForm?.targetSheetId || ''}`.trim();
      if (!id || !targetSheetId || seen.has(id)) return null;
      seen.add(id);

      const mode = ACTION_CONFIG_MODES.has(`${actionForm?.mode || ''}`.trim())
        ? `${actionForm?.mode}`.trim()
        : 'linked_form';
      const type = ACTION_FORM_TYPES.has(`${actionForm?.type || ''}`.trim())
        ? `${actionForm?.type}`.trim()
        : 'custom';
      const name = `${actionForm?.name || actionForm?.label || ''}`.trim();
      const buttonLabel = `${actionForm?.buttonLabel || ''}`.trim();
      const submitLabel = `${actionForm?.submitLabel || ''}`.trim();
      const linkedFormBehavior = ACTION_LINKED_FORM_BEHAVIORS.has(`${actionForm?.linkedFormBehavior || ''}`.trim())
        ? `${actionForm?.linkedFormBehavior}`.trim()
        : 'open_form';
      const description = `${actionForm?.description || ''}`.trim();
      const fieldMappings = Array.from(new Set(
        (Array.isArray(actionForm?.fieldMappings) ? actionForm.fieldMappings : [])
          .map((fieldMapping, fieldMappingIndex) => ({
            id: `${fieldMapping?.id || `${id}_mapping_${fieldMappingIndex + 1}`}`.trim() || `${id}_mapping_${fieldMappingIndex + 1}`,
            valueMode: ACTION_UPDATE_VALUE_MODES.has(`${fieldMapping?.valueMode || ''}`.trim())
              ? `${fieldMapping?.valueMode}`.trim()
              : (`${fieldMapping?.sourceHeaderId || ''}`.trim() ? 'sourceHeader' : 'static'),
            value: `${fieldMapping?.value ?? ''}`,
            sourceHeaderId: `${fieldMapping?.sourceHeaderId || ''}`.trim(),
            targetHeaderId: `${fieldMapping?.targetHeaderId || ''}`.trim()
          }))
          .filter((fieldMapping) => (
            fieldMapping.targetHeaderId
            && (
              fieldMapping.valueMode === 'static'
                ? true
                : fieldMapping.sourceHeaderId
            )
          ))
          .map((fieldMapping) => JSON.stringify(fieldMapping))
      )).map((fieldMapping) => JSON.parse(fieldMapping));
      const conditions = Array.from(new Set(
        (Array.isArray(actionForm?.conditions) ? actionForm.conditions : [])
          .map((condition, conditionIndex) => ({
            id: `${condition?.id || `${id}_condition_${conditionIndex + 1}`}`.trim() || `${id}_condition_${conditionIndex + 1}`,
            targetHeaderId: `${condition?.targetHeaderId || ''}`.trim(),
            operator: ACTION_UPDATE_CONDITION_OPERATORS.has(`${condition?.operator || ''}`.trim()) ? `${condition?.operator}`.trim() : 'equals',
            valueMode: ACTION_UPDATE_VALUE_MODES.has(`${condition?.valueMode || ''}`.trim()) ? `${condition?.valueMode}`.trim() : 'static',
            value: `${condition?.value ?? ''}`,
            sourceHeaderId: `${condition?.sourceHeaderId || ''}`.trim()
          }))
          .filter((condition) => condition.targetHeaderId && (condition.valueMode === 'sourceHeader' ? condition.sourceHeaderId : (condition.operator === 'isEmpty' || condition.operator === 'isNotEmpty' || `${condition.value ?? ''}` !== '')))
          .map((condition) => JSON.stringify(condition))
      )).map((condition) => JSON.parse(condition));
      const updates = Array.from(new Set(
        (Array.isArray(actionForm?.updates) ? actionForm.updates : [])
          .map((update, updateIndex) => ({
            id: `${update?.id || `${id}_update_${updateIndex + 1}`}`.trim() || `${id}_update_${updateIndex + 1}`,
            targetHeaderId: `${update?.targetHeaderId || ''}`.trim(),
            valueMode: ACTION_UPDATE_VALUE_MODES.has(`${update?.valueMode || ''}`.trim()) ? `${update?.valueMode}`.trim() : 'static',
            value: `${update?.value ?? ''}`,
            sourceHeaderId: `${update?.sourceHeaderId || ''}`.trim()
          }))
          .filter((update) => update.targetHeaderId && (update.valueMode === 'sourceHeader' ? update.sourceHeaderId : `${update.value ?? ''}` !== ''))
          .map((update) => JSON.stringify(update))
      )).map((update) => JSON.parse(update));

      return {
        id,
        mode,
        type,
        name: name || undefined,
        buttonLabel: buttonLabel || undefined,
        submitLabel: submitLabel || undefined,
        linkedFormBehavior: linkedFormBehavior || undefined,
        description: description || undefined,
        allowTargetRowSelection: linkedFormBehavior === 'open_form' && actionForm?.allowTargetRowSelection === true ? true : undefined,
        targetSheetId,
        fieldMappings,
        conditions,
        updates
      };
    })
    .filter(Boolean);
};
const sanitizeSheetRenderPresetConfig = (renderPreset, config = {}, headers = []) => {
  if (renderPreset === 'profiles') {
    const sanitizedHeaders = sanitizeHeaders(headers);
    const validHeaderIds = new Set(sanitizedHeaders.map((header) => header.id));
    const validProfileHeaderIds = new Set(
      sanitizedHeaders
        .filter((header) => header.type === 'profile')
        .map((header) => header.id)
    );
    const profileHeaderId = `${config?.profileHeaderId || ''}`.trim();
    const bodyHeaderId = `${config?.bodyHeaderId || ''}`.trim();
    const actionHeaderId = `${config?.actionHeaderId || ''}`.trim();

    return profileHeaderId && validProfileHeaderIds.has(profileHeaderId)
      ? {
          profileHeaderId,
          bodyHeaderId: validHeaderIds.has(bodyHeaderId) ? bodyHeaderId : undefined,
          actionHeaderId: validHeaderIds.has(actionHeaderId) ? actionHeaderId : undefined,
          uiColumns: normalizePresetUiColumns(config?.uiColumns, 3)
        }
      : undefined;
  }

  if (renderPreset === 'list-card') {
    const sanitizedHeaders = sanitizeHeaders(headers);
    const validHeaderIds = new Set(sanitizedHeaders.map((header) => header.id));
    const validCategoryHeaderIds = new Set(
      sanitizedHeaders
        .filter((header) => !['multiselect', 'list', 'image', 'textarea'].includes(header.type))
        .map((header) => header.id)
    );
    const validTagHeaderIds = new Set(
      sanitizedHeaders
        .filter((header) => header.type === 'list' || header.type === 'multiselect')
        .map((header) => header.id)
    );
    const imageHeaderId = `${config?.imageHeaderId || ''}`.trim();
    const categoryHeaderId = `${config?.categoryHeaderId || ''}`.trim();
    const titleHeaderId = `${config?.titleHeaderId || ''}`.trim();
    const bodyHeaderId = `${config?.bodyHeaderId || ''}`.trim();
    const tagHeaderId = `${config?.tagHeaderId || ''}`.trim();
    const nextConfig = {
      imageHeaderId: validHeaderIds.has(imageHeaderId) ? imageHeaderId : undefined,
      categoryHeaderId: validCategoryHeaderIds.has(categoryHeaderId) ? categoryHeaderId : undefined,
      titleHeaderId: validHeaderIds.has(titleHeaderId) ? titleHeaderId : undefined,
      bodyHeaderId: validHeaderIds.has(bodyHeaderId) ? bodyHeaderId : undefined,
      tagHeaderId: validTagHeaderIds.has(tagHeaderId) ? tagHeaderId : undefined,
      imageLayout: normalizeListCardImageLayout(config?.imageLayout),
      uiColumns: normalizePresetUiColumns(config?.uiColumns, 3)
    };

    return nextConfig.imageHeaderId || nextConfig.categoryHeaderId || nextConfig.titleHeaderId || nextConfig.bodyHeaderId || nextConfig.tagHeaderId
      ? nextConfig
      : undefined;
  }

  if (renderPreset === 'kanban') {
    const sanitizedHeaders = sanitizeHeaders(headers);
    const validHeaderIds = new Set(sanitizedHeaders.map((header) => header.id));
    const validStatusHeaderIds = new Set(
      sanitizedHeaders
        .filter((header) => ['select', 'buttons', 'yesno', 'rog', 'text'].includes(header.type))
        .map((header) => header.id)
    );
    const nextConfig = {
      statusHeaderId: validStatusHeaderIds.has(`${config?.statusHeaderId || ''}`.trim()) ? `${config?.statusHeaderId}`.trim() : undefined,
      titleHeaderId: validHeaderIds.has(`${config?.titleHeaderId || ''}`.trim()) ? `${config?.titleHeaderId}`.trim() : undefined,
      bodyHeaderId: validHeaderIds.has(`${config?.bodyHeaderId || ''}`.trim()) ? `${config?.bodyHeaderId}`.trim() : undefined,
      metaHeaderId: validHeaderIds.has(`${config?.metaHeaderId || ''}`.trim()) ? `${config?.metaHeaderId}`.trim() : undefined,
      uiColumns: normalizePresetUiColumns(config?.uiColumns, 3)
    };
    return nextConfig.statusHeaderId || nextConfig.titleHeaderId || nextConfig.bodyHeaderId || nextConfig.metaHeaderId
      ? nextConfig
      : undefined;
  }

  if (renderPreset === 'calendar') {
    const sanitizedHeaders = sanitizeHeaders(headers);
    const validHeaderIds = new Set(sanitizedHeaders.map((header) => header.id));
    const validDateHeaderIds = new Set(
      sanitizedHeaders
        .filter((header) => header.type === 'date' || header.type === 'datetime')
        .map((header) => header.id)
    );
    const nextConfig = {
      imageHeaderId: validHeaderIds.has(`${config?.imageHeaderId || ''}`.trim()) ? `${config?.imageHeaderId}`.trim() : undefined,
      dateHeaderId: validDateHeaderIds.has(`${config?.dateHeaderId || ''}`.trim()) ? `${config?.dateHeaderId}`.trim() : undefined,
      titleHeaderId: validHeaderIds.has(`${config?.titleHeaderId || ''}`.trim()) ? `${config?.titleHeaderId}`.trim() : undefined,
      metaHeaderId: validHeaderIds.has(`${config?.metaHeaderId || ''}`.trim()) ? `${config?.metaHeaderId}`.trim() : undefined
    };
    return nextConfig.imageHeaderId || nextConfig.dateHeaderId || nextConfig.titleHeaderId || nextConfig.metaHeaderId
      ? nextConfig
      : undefined;
  }

  return undefined;
};

const REPORT_SCOPE_VALUES = new Set(['active-row', 'sheet-overview']);
const REPORT_TONE_VALUES = new Set(['professional', 'executive', 'analytical', 'operational']);
const REPORT_FORMAT_VALUES = new Set(['markdown', 'summary']);

const sanitizeReportSkill = (reportSkill = {}, headers = []) => {
  const validHeaderIds = new Set(sanitizeHeaders(headers).map((header) => header.id));
  return {
    enabled: Boolean(reportSkill?.enabled),
    title: `${reportSkill?.title || ''}`.trim(),
    objective: `${reportSkill?.objective || ''}`.trim(),
    scope: REPORT_SCOPE_VALUES.has(`${reportSkill?.scope || ''}`.trim()) ? `${reportSkill.scope}`.trim() : 'active-row',
    tone: REPORT_TONE_VALUES.has(`${reportSkill?.tone || ''}`.trim()) ? `${reportSkill.tone}`.trim() : 'professional',
    format: REPORT_FORMAT_VALUES.has(`${reportSkill?.format || ''}`.trim()) ? `${reportSkill.format}`.trim() : 'markdown',
    focusHeaderIds: Array.from(new Set(
      (Array.isArray(reportSkill?.focusHeaderIds) ? reportSkill.focusHeaderIds : [])
        .map((headerId) => `${headerId || ''}`.trim())
        .filter((headerId) => validHeaderIds.has(headerId))
    )),
    includeVisibleRows: reportSkill?.includeVisibleRows !== false,
    includeRelatedLogs: reportSkill?.includeRelatedLogs !== false,
    includeSheetLogs: Boolean(reportSkill?.includeSheetLogs),
    maxVisibleRows: Math.max(1, Math.min(50, Number(reportSkill?.maxVisibleRows) || 15)),
    maxLogs: Math.max(1, Math.min(50, Number(reportSkill?.maxLogs) || 12)),
    instructions: `${reportSkill?.instructions || ''}`.trim()
  };
};

const sanitizeAiSkills = (aiSkills = {}, headers = []) => {
  const report = sanitizeReportSkill(aiSkills?.report, headers);
  return {
    report
  };
};

const sanitizeAiAutomations = (aiAutomations = [], headers = []) => {
  const validHeaderIds = new Set(sanitizeHeaders(headers).map((header) => header.id));
  const validTypes = new Set(['report', 'email_digest']);
  const validTriggers = new Set(['manual', 'schedule']);
  const validCadences = new Set(['daily', 'weekly']);
  const validDays = new Set(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);

  return (Array.isArray(aiAutomations) ? aiAutomations : [])
    .filter((automation) => automation && typeof automation === 'object')
    .map((automation, index) => {
      const type = validTypes.has(`${automation?.type || ''}`.trim()) ? `${automation.type}`.trim() : 'report';
      const triggerType = validTriggers.has(`${automation?.triggerType || ''}`.trim()) ? `${automation.triggerType}`.trim() : 'manual';
      const scheduleCadence = validCadences.has(`${automation?.scheduleCadence || ''}`.trim()) ? `${automation.scheduleCadence}`.trim() : 'daily';
      const scheduleDay = validDays.has(`${automation?.scheduleDay || ''}`.trim()) ? `${automation.scheduleDay}`.trim() : 'fri';

      return {
        id: `${automation?.id || `automation_${index + 1}`}`.trim(),
        enabled: automation?.enabled !== false,
        name: `${automation?.name || ''}`.trim(),
        type,
        triggerType,
        scheduleCadence,
        scheduleDay,
        scheduleTime: `${automation?.scheduleTime || ''}`.trim() || '07:00',
        timezone: `${automation?.timezone || ''}`.trim() || 'Africa/Johannesburg',
        scope: REPORT_SCOPE_VALUES.has(`${automation?.scope || ''}`.trim()) ? `${automation.scope}`.trim() : (type === 'email_digest' ? 'sheet-overview' : 'active-row'),
        tone: REPORT_TONE_VALUES.has(`${automation?.tone || ''}`.trim()) ? `${automation.tone}`.trim() : 'professional',
        format: REPORT_FORMAT_VALUES.has(`${automation?.format || ''}`.trim()) ? `${automation.format}`.trim() : 'markdown',
        focusHeaderIds: Array.from(new Set(
          (Array.isArray(automation?.focusHeaderIds) ? automation.focusHeaderIds : [])
            .map((headerId) => `${headerId || ''}`.trim())
            .filter((headerId) => validHeaderIds.has(headerId))
        )),
        includeVisibleRows: automation?.includeVisibleRows !== false,
        includeRelatedLogs: automation?.includeRelatedLogs !== false,
        includeSheetLogs: Boolean(automation?.includeSheetLogs),
        maxVisibleRows: Math.max(1, Math.min(50, Number(automation?.maxVisibleRows) || (type === 'email_digest' ? 25 : 15))),
        maxLogs: Math.max(1, Math.min(50, Number(automation?.maxLogs) || 12)),
        instructions: `${automation?.instructions || ''}`.trim(),
        recipients: normalizeEmails(automation?.recipients || [])
      };
    })
    .filter((automation) => automation.id && automation.name);
};

const sanitizeRowValues = (values = {}, headers = []) => {
  const safeHeaders = sanitizeHeaders(headers);
  const rawValues = safeHeaders.reduce((acc, header) => {
    if (header.type === 'checkbox') {
      acc[header.id] = Boolean(values?.[header.id]);
      return acc;
    }
    if (header.type === 'multiselect' || header.type === 'list') {
      acc[header.id] = Array.isArray(values?.[header.id])
        ? values[header.id].map((item) => `${item || ''}`.trim()).filter(Boolean)
        : `${values?.[header.id] || ''}`.split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      return acc;
    }
    if (header.type === 'number' || header.type === 'rating' || header.type === 'currency' || header.type === 'percent') {
      const raw = values?.[header.id];
      acc[header.id] = raw === '' || raw === null || raw === undefined ? '' : Number(raw);
      return acc;
    }
    acc[header.id] = `${values?.[header.id] ?? ''}`;
    return acc;
  }, {});

  return resolveRowFormulas(safeHeaders, rawValues);
};

const isOwnRowsMode = (sheetData) =>
  sheetData?.visibility === 'restricted' && sheetData?.collaboratorAccess === 'edit_own_rows';

const isViewAllEditOwnMode = (sheetData) =>
  sheetData?.visibility === 'restricted' && sheetData?.collaboratorAccess === 'view_all_edit_own';

const isSharedEditMode = (sheetData) =>
  sheetData?.visibility === 'restricted' && sheetData?.collaboratorAccess === 'edit';

const loadCurrentUserSheetShare = async (sheetId) => {
  const currentUserId = auth.currentUser?.uid || null;
  if (!currentUserId) return null;

  try {
    const shareSnap = await getDoc(getSheetShareUserRef(sheetId, currentUserId));
    if (!shareSnap.exists()) return null;
    return {
      userId: currentUserId,
      ...shareSnap.data()
    };
  } catch (error) {
    console.warn('Failed to load current user sheet share:', sheetId, error);
    return null;
  }
};

const getSharedRowUserRef = (sheetId, userId) =>
  doc(db, SHEETS_COLLECTION, sheetId, SHARED_ROW_USERS_SUBCOLLECTION, userId);

const getSharedRowItemsCollection = (sheetId, userId) =>
  collection(db, SHEETS_COLLECTION, sheetId, SHARED_ROW_USERS_SUBCOLLECTION, userId, SHARED_ROW_ITEMS_SUBCOLLECTION);

const normalizeSharedRowDoc = (item, contributorId) => ({
  id: item.id,
  ...item.data(),
  contributorId,
  storageMode: 'shared-own-rows'
});

const sortRowsByCreatedAt = (rows = []) =>
  [...rows].sort((left, right) => {
    const leftTime = left.createdAt?.toMillis?.() || new Date(left.createdAt || 0).getTime();
    const rightTime = right.createdAt?.toMillis?.() || new Date(right.createdAt || 0).getTime();
    return leftTime - rightTime;
  });

const getSheetShareUserRef = (sheetId, userId) =>
  doc(db, SHEETS_COLLECTION, sheetId, SHEET_SHARE_USERS_SUBCOLLECTION, userId);

const getSheetShareEmailRef = (sheetId, email) =>
  doc(db, SHEETS_COLLECTION, sheetId, SHEET_SHARE_EMAILS_SUBCOLLECTION, `${email || ''}`.trim().toLowerCase());

const getSheetUserShareIndexRef = (sheetId, userId) =>
  doc(db, SHEET_USER_SHARES_COLLECTION, `${sheetId}_${userId}`);

const getSheetLogsCollection = (sheetId) =>
  collection(db, SHEETS_COLLECTION, sheetId, SHEET_LOGS_SUBCOLLECTION);

const getSheetItemRef = (sheetId, rowId) =>
  doc(db, SHEETS_COLLECTION, sheetId, SHEET_ITEMS_SUBCOLLECTION, rowId);

const getSharedRowItemRef = (sheetId, contributorId, rowId) =>
  doc(db, SHEETS_COLLECTION, sheetId, SHARED_ROW_USERS_SUBCOLLECTION, contributorId, SHARED_ROW_ITEMS_SUBCOLLECTION, rowId);

const getRowScopedRef = (sheetId, rowData = {}) => {
  const rowId = `${rowData?.rowId || rowData?.id || ''}`.trim();
  if (!rowId) {
    throw new Error('A row id is required for row-scoped data.');
  }
  if (rowData?.storageMode === 'shared-own-rows') {
    const contributorId = `${rowData?.contributorId || ''}`.trim();
    if (!contributorId) {
      throw new Error('A contributor id is required for shared row data.');
    }
    return getSharedRowItemRef(sheetId, contributorId, rowId);
  }
  return getSheetItemRef(sheetId, rowId);
};

const getRowLogsCollection = (sheetId, rowData = {}) =>
  collection(getRowScopedRef(sheetId, rowData), SHEET_LOGS_SUBCOLLECTION);

const getRowAiThreadsCollection = (sheetId, rowData = {}) =>
  collection(getRowScopedRef(sheetId, rowData), SHEET_AI_THREADS_SUBCOLLECTION);

const getRowAiThreadRef = (sheetId, rowData = {}, threadId) =>
  doc(getRowAiThreadsCollection(sheetId, rowData), threadId);

const getRowAiMessagesCollection = (sheetId, rowData = {}, threadId) =>
  collection(getRowAiThreadRef(sheetId, rowData, threadId), SHEET_AI_MESSAGES_SUBCOLLECTION);

const getWorkspaceAiChatsCollection = (workspaceId, userId) =>
  collection(
    db,
    'docs-sheet-workspaces',
    workspaceId,
    WORKSPACE_AI_CHAT_USERS_SUBCOLLECTION,
    userId,
    WORKSPACE_AI_CHATS_SUBCOLLECTION
  );

const sanitizeSheetLog = (logData = {}, context = {}) => {
  const scopeType = SHEET_LOG_SCOPE_TYPES.has(`${logData.scopeType || ''}`.trim())
    ? `${logData.scopeType}`.trim()
    : 'row';
  const note = `${logData.note || logData.noteUpdate || ''}`.trim();
  if (!note) {
    throw new Error('A log note or update is required.');
  }

  const progressValue = logData.progress === '' || logData.progress === null || logData.progress === undefined
    ? ''
    : Number(logData.progress);
  const progress = progressValue === ''
    ? ''
    : Number.isFinite(progressValue)
      ? Math.max(0, Math.min(100, Math.round(progressValue)))
      : '';
  const status = SHEET_LOG_STATUSES.has(`${logData.status || ''}`.trim())
    ? `${logData.status}`.trim()
    : 'O';
  const action = SHEET_LOG_ACTIONS.has(`${logData.action || ''}`.trim())
    ? `${logData.action}`.trim()
    : 'Call';
  const date = `${logData.date || ''}`.trim() || new Date().toISOString().slice(0, 10);
  const columnId = scopeType === 'column' ? `${logData.columnId || ''}`.trim() : '';
  const rowId = scopeType === 'row' ? `${logData.rowId || ''}`.trim() : '';

  return stripUndefinedDeep({
    sheetId: context.sheetId,
    scopeType,
    rowId: rowId || undefined,
    columnId: columnId || undefined,
    targetLabel: `${logData.targetLabel || ''}`.trim() || undefined,
    date,
    note,
    progress,
    status,
    action,
    createdBy: context.userId || null,
    createdByEmail: context.userEmail || null,
    updatedBy: context.userId || null
  });
};

const sanitizeSheetAiMessage = (message = {}, context = {}) => {
  const role = ['user', 'assistant'].includes(`${message.role || ''}`.trim())
    ? `${message.role}`.trim()
    : 'user';
  const content = `${message.content || ''}`.trim();
  if (!content) {
    throw new Error('Message content is required.');
  }

  return stripUndefinedDeep({
    sheetId: context.sheetId,
    threadId: context.threadId,
    role,
    content,
    createdBy: context.userId || null,
    createdByEmail: context.userEmail || null
  });
};

const resolveRestrictedShareTargets = async (allowedEmails = [], selectedUserIds = []) => {
  const normalizedEmails = normalizeEmails(allowedEmails);
  if (!normalizedEmails.length) {
    return {
      allowedEmails: [],
      allowedUserIds: [],
      sharedUsers: []
    };
  }

  const matchedUsers = await userService.findUsersByEmails(normalizedEmails);
  const selectedIds = normalizeUserIds(selectedUserIds);
  const matchedSharedUsers = matchedUsers
    .filter((user) => normalizedEmails.includes(`${user.email || ''}`.trim().toLowerCase()))
    .map((user) => ({
      uid: user.id || user.uid,
      email: `${user.email || ''}`.trim().toLowerCase(),
      displayName: `${user.displayName || ''}`.trim(),
      approvedOrganizationIds: normalizeOrganizationIds(user.approvedOrganizationIds)
    }))
    .filter((user) => user.uid && user.email);

  const allowedUserIds = selectedIds.length
    ? matchedSharedUsers.filter((user) => selectedIds.includes(user.uid)).map((user) => user.uid)
    : matchedSharedUsers.map((user) => user.uid);

  return {
    allowedEmails: normalizedEmails,
    allowedUserIds,
    sharedUsers: matchedSharedUsers
  };
};

const restrictShareTargetsToOrganizations = (shareTargets, organizationIds = []) => {
  const requiredIds = normalizeOrganizationIds(organizationIds);
  if (!requiredIds.length) return shareTargets;
  const sharedUsers = (shareTargets.sharedUsers || []).filter((user) => (
    normalizeOrganizationIds(user.approvedOrganizationIds).some((organizationId) => requiredIds.includes(organizationId))
  ));
  const allowedUserIds = sharedUsers.map((user) => user.uid);
  const allowedEmails = sharedUsers.map((user) => user.email);
  return { sharedUsers, allowedUserIds, allowedEmails };
};

const commitBatchOperations = async (operations = []) => {
  if (!operations.length) return;

  for (let index = 0; index < operations.length; index += 400) {
    const batch = writeBatch(db);
    operations.slice(index, index + 400).forEach((operation) => {
      if (operation.type === 'set') {
        batch.set(operation.ref, operation.data, operation.options || {});
      }
      if (operation.type === 'delete') {
        batch.delete(operation.ref);
      }
    });
    await batch.commit();
  }
};

const syncSheetShares = async (sheetId, shareTargets, collaboratorAccess, ownerId) => {
  const [existingUserShares, existingEmailShares] = await Promise.all([
    getDocs(collection(db, SHEETS_COLLECTION, sheetId, SHEET_SHARE_USERS_SUBCOLLECTION)),
    getDocs(collection(db, SHEETS_COLLECTION, sheetId, SHEET_SHARE_EMAILS_SUBCOLLECTION))
  ]);

  const desiredUserShares = new Map(
    (shareTargets.sharedUsers || [])
      .filter((item) => (shareTargets.allowedUserIds || []).includes(item.uid))
      .map((item) => [item.uid, item])
  );
  const desiredEmailShares = new Set(shareTargets.allowedEmails || []);

  const operations = [];

  existingUserShares.docs.forEach((docSnap) => {
    if (!desiredUserShares.has(docSnap.id)) {
      operations.push({ type: 'delete', ref: docSnap.ref });
      operations.push({ type: 'delete', ref: getSheetUserShareIndexRef(sheetId, docSnap.id) });
    }
  });

  existingEmailShares.docs.forEach((docSnap) => {
    if (!desiredEmailShares.has(docSnap.id)) {
      operations.push({ type: 'delete', ref: docSnap.ref });
    }
  });

  desiredUserShares.forEach((item, userId) => {
    operations.push({
      type: 'set',
      ref: getSheetShareUserRef(sheetId, userId),
      data: stripUndefinedDeep({
        sheetId,
        userId,
        email: item.email,
        displayName: item.displayName || '',
        access: collaboratorAccess,
        grantedBy: ownerId,
        updatedAt: serverTimestamp()
      }),
      options: { merge: true }
    });
    operations.push({
      type: 'set',
      ref: getSheetUserShareIndexRef(sheetId, userId),
      data: stripUndefinedDeep({
        sheetId,
        userId,
        ownerId,
        access: collaboratorAccess,
        updatedAt: serverTimestamp()
      }),
      options: { merge: true }
    });
  });

  desiredEmailShares.forEach((email) => {
    operations.push({
      type: 'set',
      ref: getSheetShareEmailRef(sheetId, email),
      data: stripUndefinedDeep({
        sheetId,
        email,
        access: collaboratorAccess,
        grantedBy: ownerId,
        updatedAt: serverTimestamp()
      }),
      options: { merge: true }
    });
  });

  await commitBatchOperations(operations);
};

const clearSheetShares = async (sheetId) => {
  const [userShares, emailShares] = await Promise.all([
    getDocs(collection(db, SHEETS_COLLECTION, sheetId, SHEET_SHARE_USERS_SUBCOLLECTION)),
    getDocs(collection(db, SHEETS_COLLECTION, sheetId, SHEET_SHARE_EMAILS_SUBCOLLECTION))
  ]);

  const operations = [
    ...userShares.docs.flatMap((docSnap) => ([
      { type: 'delete', ref: docSnap.ref },
      { type: 'delete', ref: getSheetUserShareIndexRef(sheetId, docSnap.id) }
    ])),
    ...emailShares.docs.map((docSnap) => ({ type: 'delete', ref: docSnap.ref }))
  ];

  await commitBatchOperations(operations);
};

const loadSheetShares = async (sheetId) => {
  const [userShares, emailShares] = await Promise.all([
    getDocs(collection(db, SHEETS_COLLECTION, sheetId, SHEET_SHARE_USERS_SUBCOLLECTION)),
    getDocs(collection(db, SHEETS_COLLECTION, sheetId, SHEET_SHARE_EMAILS_SUBCOLLECTION))
  ]);

  return {
    allowedEmails: emailShares.docs.map((docSnap) => docSnap.id),
    allowedUserIds: userShares.docs.map((docSnap) => docSnap.id),
    sharedUsers: userShares.docs.map((docSnap) => ({
      uid: docSnap.id,
      ...docSnap.data()
    }))
  };
};

const sortSheetsByUpdatedAtDesc = (items = []) => [...items].sort((left, right) => {
  const leftTime = left?.updatedAt?.toMillis?.()
    || left?.createdAt?.toMillis?.()
    || new Date(left?.updatedAt || left?.createdAt || 0).getTime();
  const rightTime = right?.updatedAt?.toMillis?.()
    || right?.createdAt?.toMillis?.()
    || new Date(right?.updatedAt || right?.createdAt || 0).getTime();
  return rightTime - leftTime;
});

const resolveWorkspaceForSheet = async (sheetData = {}) => {
  const requestedWorkspaceId = `${sheetData?.workspaceId || ''}`.trim();
  if (requestedWorkspaceId) {
    const workspace = await sheetWorkspaceService.getWorkspaceById(requestedWorkspaceId);
    return {
      workspaceId: workspace.id,
      workspaceTitle: `${workspace.title || ''}`.trim(),
      workspaceIsTemplate: workspace.isTemplate === true
    };
  }

  if (sheetData?.documentId && !sheetData?.forceCreate) {
    const existingSnap = await getDoc(doc(db, SHEETS_COLLECTION, sheetData.documentId));
    if (existingSnap.exists()) {
      const existingData = existingSnap.data();
      const existingWorkspaceId = `${existingData?.workspaceId || ''}`.trim();
      if (existingWorkspaceId) {
        return {
          workspaceId: existingWorkspaceId,
          workspaceTitle: `${existingData?.workspaceTitle || ''}`.trim(),
          workspaceIsTemplate: false
        };
      }
    }
  }

  return {
    workspaceId: '',
    workspaceTitle: '',
    workspaceIsTemplate: false
  };
};

const getTemplateWorkspaceState = async (workspaceId = '') => {
  const normalizedWorkspaceId = `${workspaceId || ''}`.trim();
  if (!normalizedWorkspaceId) return false;
  const workspace = await sheetWorkspaceService.getWorkspaceById(normalizedWorkspaceId);
  return workspace?.isTemplate === true;
};

const applyTemplateWorkspaceSheetDefaults = (sheetData = {}, workspaceIsTemplate = false) => {
  if (!workspaceIsTemplate) return { ...sheetData };
  return {
    ...sheetData,
    visibility: 'public',
    collaboratorAccess: 'view',
    allowedEmails: [],
    allowedUserIds: []
  };
};

const validateWorkspaceScopedReferences = async (workspaceId, headers = [], customLogSheetId = '', currentSheetId = '', actionForms = []) => {
  const scopedWorkspaceId = `${workspaceId || ''}`.trim();
  if (!scopedWorkspaceId) return;

  const extractFormulaSheetId = (formula) => {
    const config = parseFormulaToConfig(formula, headers);
    return config?.type === 'lookup' ? `${config.lookupSheetId || ''}`.trim() : '';
  };

  const referencedSheetIds = Array.from(new Set([
    ...headers
      .map((header) => normalizeSheetOptionSource(header?.optionSource)?.sheetId || '')
      .filter(Boolean),
    ...headers.flatMap((header) => (
      (Array.isArray(header?.conditionalOptions) ? header.conditionalOptions : [])
        .map((rule) => normalizeSheetOptionSource(rule?.optionSource)?.sheetId || '')
        .filter(Boolean)
    )),
    ...headers
      .map((header) => extractFormulaSheetId(header?.formula || ''))
      .filter(Boolean),
    ...headers.flatMap((header) => (
      (Array.isArray(header?.conditionalFormulaRules) ? header.conditionalFormulaRules : [])
        .map((rule) => extractFormulaSheetId(rule?.formula || ''))
        .filter(Boolean)
    )),
    `${customLogSheetId || ''}`.trim(),
    ...sanitizeActionForms(actionForms).map((actionForm) => actionForm.targetSheetId)
  ].filter(Boolean)));

  if (!referencedSheetIds.length) return;

  await Promise.all(referencedSheetIds.map(async (referencedSheetId) => {
    if (referencedSheetId === currentSheetId || referencedSheetId === ACTION_UPDATE_TARGET_CURRENT_SHEET) return;
    const targetSnap = await getDoc(doc(db, SHEETS_COLLECTION, referencedSheetId));
    if (!targetSnap.exists()) {
      throw new Error('One or more referenced sheets no longer exist.');
    }

    const targetWorkspaceId = `${targetSnap.data()?.workspaceId || ''}`.trim();
    if (targetWorkspaceId && targetWorkspaceId !== scopedWorkspaceId) {
      throw new Error('Cross-sheet references, custom log sheets, and action forms must stay inside the same workspace.');
    }
  }));
};

export const sheetService = {
  async saveSheet(sheetData, userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to save a sheet');

    const { workspaceId, workspaceTitle, workspaceIsTemplate } = await resolveWorkspaceForSheet(sheetData);
    const normalizedSheetData = applyTemplateWorkspaceSheetDefaults(sheetData, workspaceIsTemplate);
    const organizationIds = normalizeOrganizationIds(normalizedSheetData.organizationIds);
    const visibility = organizationIds.length ? 'restricted' : sanitizeVisibility(normalizedSheetData.visibility);
    const collaboratorAccess = visibility === 'restricted'
      ? sanitizeCollaboratorAccess(normalizedSheetData.collaboratorAccess)
      : 'view';
    const headers = sanitizeHeaders(normalizedSheetData.headers || []);
    const filterHeaderIds = sanitizeFilterHeaderIds(normalizedSheetData.filterHeaderIds || [], headers);
    const actionForms = sanitizeActionForms(normalizedSheetData.actionForms || []);
    const renderPreset = sanitizeSheetRenderPreset(normalizedSheetData.renderPreset);
    const renderPresetConfig = sanitizeSheetRenderPresetConfig(renderPreset, normalizedSheetData.renderPresetConfig, headers);
    const primaryColor = normalizeSheetThemeColor(normalizedSheetData.primaryColor);
    const secondaryColor = normalizeSheetThemeColor(normalizedSheetData.secondaryColor);
    const ogImageUrl = normalizeSheetOgImageUrl(normalizedSheetData.ogImageUrl);
    const rawShareTargets = visibility === 'restricted'
      ? await resolveRestrictedShareTargets(normalizedSheetData.allowedEmails || [], normalizedSheetData.allowedUserIds || [])
      : { allowedEmails: [], allowedUserIds: [], sharedUsers: [] };
    const shareTargets = restrictShareTargetsToOrganizations(rawShareTargets, organizationIds);

    if (!headers.length) {
      throw new Error('Add at least one header before saving');
    }

    const isWorkspaceOnly = Boolean(workspaceId) || normalizedSheetData.workspaceOnly === true;
    await validateWorkspaceScopedReferences(
      workspaceId,
      headers,
      `${normalizedSheetData.customLogSheetId || ''}`.trim(),
      `${normalizedSheetData.documentId || ''}`.trim(),
      actionForms
    );

    const sheetsRef = collection(db, SHEETS_COLLECTION);
    const docRef = normalizedSheetData.documentId
      ? doc(db, SHEETS_COLLECTION, normalizedSheetData.documentId)
      : doc(sheetsRef);
    const documentId = docRef.id;
    const existingDoc = (normalizedSheetData.documentId && !normalizedSheetData.forceCreate) ? await getDoc(docRef) : null;
    const isExistingSheet = existingDoc?.exists?.() === true;
    const sheetOwnerId = isExistingSheet
      ? `${existingDoc.data()?.userId || ''}`.trim() || ownerId
      : ownerId;

    const payload = stripUndefinedDeep({
      title: normalizeSheetTitle(normalizedSheetData.title),
      titleKey: getSheetTitleKey(normalizedSheetData.title),
      description: `${normalizedSheetData.description || ''}`.trim(),
      customLogSheetId: `${normalizedSheetData.customLogSheetId || ''}`.trim() || undefined,
      workspaceId: workspaceId || undefined,
      workspaceTitle: workspaceTitle || undefined,
      workspaceOnly: isWorkspaceOnly,
      organizationIds,
      documentId,
      originalDocumentId: normalizedSheetData.originalDocumentId || documentId,
      userId: sheetOwnerId,
      visibility,
      collaboratorAccess,
      adminBuilderAccessEnabled: sanitizeAdminBuilderAccessEnabled(normalizedSheetData.adminBuilderAccessEnabled),
      isLogSheet: sanitizeLogSheetFlag(normalizedSheetData.isLogSheet),
      isTemplate: sanitizeTemplateFlag(normalizedSheetData.isTemplate),
      templateClonedFromId: `${normalizedSheetData.templateClonedFromId || ''}`.trim() || undefined,
      templateClonedFromTitle: `${normalizedSheetData.templateClonedFromTitle || ''}`.trim() || undefined,
      primaryColor,
      secondaryColor,
      renderFont: normalizeSheetRenderFont(normalizedSheetData.renderFont),
      ogImageUrl,
      renderPreset,
      renderPresetConfig,
      headers,
      filterHeaderIds,
      actionForms,
      analytics: sanitizeAnalytics(normalizedSheetData.analytics || [], headers),
      charts: [],
      aiSkills: sanitizeAiSkills(normalizedSheetData.aiSkills || {}, headers),
      aiAutomations: sanitizeAiAutomations(normalizedSheetData.aiAutomations || [], headers),
      sheetType: 'smart',
      updatedAt: serverTimestamp()
    });

    if (!payload.title) {
      throw new Error('Sheet title is required');
    }

    const writePayload = {
      ...payload,
      createdAt: serverTimestamp()
    };

    if (normalizedSheetData.forceCreate) {
      await withSheetWriteStep('Main sheet document create', () => setDoc(docRef, writePayload));
    } else if (normalizedSheetData.documentId) {
      if (existingDoc.exists()) {
        await withSheetWriteStep('Main sheet document update', () => updateDoc(docRef, payload));
      } else {
        await withSheetWriteStep('Main sheet document create', () => setDoc(docRef, writePayload));
      }
    } else {
      await withSheetWriteStep('Main sheet document create', () => setDoc(docRef, writePayload));
    }

    if (!isExistingSheet && workspaceId) {
      await withSheetWriteStep('Workspace sheet count update', () => (
        sheetWorkspaceService.updateWorkspace(workspaceId, {
          sheetCount: increment(1)
        })
      ));
    }

    if (visibility === 'restricted') {
      await withSheetWriteStep('Restricted sheet share sync', () => (
        syncSheetShares(documentId, shareTargets, collaboratorAccess, sheetOwnerId)
      ));
    } else if (isExistingSheet) {
      await withSheetWriteStep('Sheet share cleanup', () => clearSheetShares(documentId));
    }

    return {
      id: documentId,
      ...payload,
      ...shareTargets,
      primaryColor,
      secondaryColor,
      renderFont: normalizeSheetRenderFont(normalizedSheetData.renderFont),
      ogImageUrl,
      renderPreset,
      renderPresetConfig,
      filterHeaderIds,
      actionForms,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  },

  async publishSheet(sheetId, publishData = {}, userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to publish a sheet');

    const existingSheetSnap = await getDoc(doc(db, SHEETS_COLLECTION, sheetId));
    if (!existingSheetSnap.exists()) throw new Error('Sheet not found');
    const sheetOwnerId = `${existingSheetSnap.data()?.userId || ''}`.trim() || ownerId;

    const organizationIds = normalizeOrganizationIds(publishData.organizationIds);
    const visibility = organizationIds.length ? 'restricted' : sanitizeVisibility(publishData.visibility);
    const collaboratorAccess = visibility === 'restricted'
      ? sanitizeCollaboratorAccess(publishData.collaboratorAccess)
      : 'view';
    const headers = sanitizeHeaders(publishData.headers || []);
    const filterHeaderIds = sanitizeFilterHeaderIds(publishData.filterHeaderIds || [], headers);
    const actionForms = sanitizeActionForms(publishData.actionForms || []);
    const renderPreset = sanitizeSheetRenderPreset(publishData.renderPreset);
    const renderPresetConfig = sanitizeSheetRenderPresetConfig(renderPreset, publishData.renderPresetConfig, headers);
    const primaryColor = normalizeSheetThemeColor(publishData.primaryColor);
    const secondaryColor = normalizeSheetThemeColor(publishData.secondaryColor);
    const ogImageUrl = normalizeSheetOgImageUrl(publishData.ogImageUrl);
    const { workspaceId, workspaceTitle } = await resolveWorkspaceForSheet({ ...publishData, documentId: sheetId });
    const isWorkspaceOnly = Boolean(workspaceId) || publishData.workspaceOnly === true;
    await validateWorkspaceScopedReferences(
      workspaceId,
      headers,
      `${publishData.customLogSheetId || ''}`.trim(),
      sheetId,
      actionForms
    );
    const rawShareTargets = visibility === 'restricted'
      ? await resolveRestrictedShareTargets(publishData.allowedEmails || [], publishData.allowedUserIds || [])
      : { allowedEmails: [], allowedUserIds: [], sharedUsers: [] };
    const shareTargets = restrictShareTargetsToOrganizations(rawShareTargets, organizationIds);
    const publishedRef = doc(collection(db, SHEETS_COLLECTION, sheetId, 'published'));

    await setDoc(publishedRef, stripUndefinedDeep({
      sheetId,
      originalDocumentId: publishData.originalDocumentId || sheetId,
      title: `${publishData.title || ''}`.trim(),
      description: `${publishData.description || ''}`.trim(),
      customLogSheetId: `${publishData.customLogSheetId || ''}`.trim() || undefined,
      workspaceId: workspaceId || undefined,
      workspaceTitle: workspaceTitle || undefined,
      workspaceOnly: isWorkspaceOnly,
      organizationIds,
      primaryColor,
      secondaryColor,
      ogImageUrl,
      renderPreset,
      renderPresetConfig,
      headers,
      filterHeaderIds,
      actionForms,
      analytics: sanitizeAnalytics(publishData.analytics || [], headers),
      charts: [],
      aiSkills: sanitizeAiSkills(publishData.aiSkills || {}, headers),
      aiAutomations: sanitizeAiAutomations(publishData.aiAutomations || [], headers),
      visibility,
      collaboratorAccess,
      isLogSheet: sanitizeLogSheetFlag(publishData.isLogSheet),
      adminBuilderAccessEnabled: sanitizeAdminBuilderAccessEnabled(publishData.adminBuilderAccessEnabled),
      isTemplate: sanitizeTemplateFlag(publishData.isTemplate),
      userId: visibility === 'public' ? null : sheetOwnerId,
      publishedBy: ownerId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }));

    await updateDoc(doc(db, SHEETS_COLLECTION, sheetId), stripUndefinedDeep({
      title: `${publishData.title || ''}`.trim(),
      description: `${publishData.description || ''}`.trim(),
      customLogSheetId: `${publishData.customLogSheetId || ''}`.trim() || undefined,
      workspaceId: workspaceId || undefined,
      workspaceTitle: workspaceTitle || undefined,
      workspaceOnly: isWorkspaceOnly,
      organizationIds,
      primaryColor,
      secondaryColor,
      ogImageUrl,
      renderPreset,
      renderPresetConfig,
      headers,
      filterHeaderIds,
      actionForms,
      analytics: sanitizeAnalytics(publishData.analytics || [], headers),
      charts: [],
      aiSkills: sanitizeAiSkills(publishData.aiSkills || {}, headers),
      aiAutomations: sanitizeAiAutomations(publishData.aiAutomations || [], headers),
      visibility,
      collaboratorAccess,
      isLogSheet: sanitizeLogSheetFlag(publishData.isLogSheet),
      adminBuilderAccessEnabled: sanitizeAdminBuilderAccessEnabled(publishData.adminBuilderAccessEnabled),
      isTemplate: sanitizeTemplateFlag(publishData.isTemplate),
      publishedAt: serverTimestamp(),
      latestPublishedId: publishedRef.id,
      updatedAt: serverTimestamp()
    }));

    if (visibility === 'restricted') {
      await syncSheetShares(sheetId, shareTargets, collaboratorAccess, sheetOwnerId);
    } else {
      await clearSheetShares(sheetId);
    }

    return {
      id: publishedRef.id,
      sheetId,
      visibility,
      filterHeaderIds,
      allowedEmails: shareTargets.allowedEmails,
      allowedUserIds: shareTargets.allowedUserIds,
      sharedUsers: shareTargets.sharedUsers
    };
  },

  async getUserSheets(userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to load sheets');

    const q = query(
      collection(db, SHEETS_COLLECTION),
      where('userId', '==', ownerId),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => ({
      id: item.id,
      ...item.data()
    }));
  },

  async getSharedSheets(userId) {
    const currentUserId = userId || auth.currentUser?.uid;
    if (!currentUserId) {
      throw new Error('User is required to load shared sheets');
    }

    const shareSnapshot = await getDocs(query(
      collection(db, SHEET_USER_SHARES_COLLECTION),
      where('userId', '==', currentUserId)
    ));

    const sharedSheets = await Promise.all(
      shareSnapshot.docs.map(async (shareDoc) => {
        const shareData = shareDoc.data();
        if (!shareData?.sheetId) return null;

        try {
          const sheetSnap = await getDoc(doc(db, SHEETS_COLLECTION, shareData.sheetId));
          if (!sheetSnap.exists()) return null;
          const sheetData = sheetSnap.data();
          if (sheetData.userId === currentUserId) return null;
          return {
            id: sheetSnap.id,
            ...sheetData,
            sharedAccess: shareData.access || sheetData.collaboratorAccess || 'view',
            sharedVia: 'user'
          };
        } catch (error) {
          console.warn('Failed to load shared sheet:', shareData.sheetId, error);
          return null;
        }
      })
    );

    const directSheets = sharedSheets.filter(Boolean);
    const userProfile = await userService.getUserProfile(currentUserId).catch(() => null);
    const organizationIds = normalizeOrganizationIds(userProfile?.approvedOrganizationIds);
    const organizationSnapshots = await Promise.all(organizationIds.map((organizationId) => (
      getDocs(query(
        collection(db, SHEETS_COLLECTION),
        where('organizationIds', 'array-contains', organizationId),
        orderBy('updatedAt', 'desc')
      )).catch(() => null)
    )));
    const organizationSheets = organizationSnapshots
      .filter(Boolean)
      .flatMap((snapshot) => snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
        sharedAccess: item.data().collaboratorAccess || 'view',
        sharedVia: 'organization'
      })))
      .filter((sheet) => sheet.userId !== currentUserId);

    return sortSheetsByUpdatedAtDesc(
      [...directSheets, ...organizationSheets].reduce((output, sheet) => {
        if (!sheet?.id || output.some((item) => item.id === sheet.id)) return output;
        output.push(sheet);
        return output;
      }, [])
    );
  },

  async getAdminAccessibleSheets() {
    const [snapshot, adminWorkspaces] = await Promise.all([
      getDocs(query(
        collection(db, SHEETS_COLLECTION),
        where('adminBuilderAccessEnabled', '==', true)
      )),
      sheetWorkspaceService.getAdminAccessibleWorkspaces()
    ]);
    const directSheets = snapshot.docs.map((item) => ({
      id: item.id,
      ...item.data(),
      adminAccessSource: 'sheet'
    }));
    const workspaceIds = adminWorkspaces.map((workspace) => workspace.id).filter(Boolean);
    const workspaceSnapshots = await Promise.all(workspaceIds.map((workspaceId) => getDocs(query(
      collection(db, SHEETS_COLLECTION),
      where('workspaceId', '==', workspaceId)
    ))));
    const workspaceSheets = workspaceSnapshots.flatMap((workspaceSnapshot) => (
      workspaceSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }))
    ));

    return sortSheetsByUpdatedAtDesc(
      [...directSheets, ...workspaceSheets.map((sheet) => ({
        ...sheet,
        adminAccessSource: sheet.adminBuilderAccessEnabled === true ? 'sheet' : 'workspace'
      }))].reduce((output, sheet) => {
        if (!sheet?.id || output.some((item) => item.id === sheet.id)) return output;
        output.push(sheet);
        return output;
      }, [])
    );
  },

  async getWorkspaceSheets(workspaceIds = [], userId) {
    const currentUserId = userId || auth.currentUser?.uid;
    if (!currentUserId) {
      throw new Error('User is required to load workspace sheets');
    }

    const normalizedWorkspaceIds = Array.from(new Set(
      (Array.isArray(workspaceIds) ? workspaceIds : [])
        .map((workspaceId) => `${workspaceId || ''}`.trim())
        .filter(Boolean)
    ));

    if (!normalizedWorkspaceIds.length) return [];

    const [ownedSheets, sharedSheets] = await Promise.all([
      this.getUserSheets(currentUserId).catch(() => []),
      this.getSharedSheets(currentUserId).catch(() => [])
    ]);

    return sortSheetsByUpdatedAtDesc(
      [...ownedSheets, ...sharedSheets]
        .filter((sheet) => normalizedWorkspaceIds.includes(`${sheet?.workspaceId || ''}`.trim()))
        .reduce((acc, sheet) => {
          if (!sheet?.id || acc.some((item) => item.id === sheet.id)) return acc;
          acc.push({
            ...sheet,
            sharedVia: sheet.userId === currentUserId ? 'owner' : (sheet.sharedVia || 'user')
          });
          return acc;
        }, [])
        .filter((sheet) => sheet.userId !== currentUserId)
    );
  },

  async getSheetsByWorkspaceIds(workspaceIds = []) {
    const normalizedWorkspaceIds = normalizeWorkspaceIds(workspaceIds);
    if (!normalizedWorkspaceIds.length) return [];

    const snapshots = await Promise.all(
      normalizedWorkspaceIds.map(async (workspaceId) => {
        const snapshot = await getDocs(query(
          collection(db, SHEETS_COLLECTION),
          where('workspaceId', '==', workspaceId)
        ));

        return snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data()
        }));
      })
    );

    return sortSheetsByUpdatedAtDesc(
      snapshots
        .flat()
        .filter((sheet) => sheet?.id)
    );
  },

  async getWorkspaceActivity(workspaceIds = [], options = {}) {
    const normalizedWorkspaceIds = normalizeWorkspaceIds(workspaceIds);
    if (!normalizedWorkspaceIds.length) return [];

    const safeLimit = Math.max(1, Math.min(50, Number(options?.maxResults) || 12));
    const sheets = Array.isArray(options?.sheets) && options.sheets.length
      ? options.sheets
      : await this.getSheetsByWorkspaceIds(normalizedWorkspaceIds);
    const sheetMap = new Map(
      sheets
        .filter((sheet) => sheet?.id)
        .map((sheet) => [sheet.id, sheet])
    );
    const sheetIds = Array.from(sheetMap.keys());

    if (!sheetIds.length) return [];

    const perSheetSnapshots = await Promise.all(
      sheetIds.map(async (sheetId) => {
        try {
          return await getDocs(query(
            collectionGroup(db, SHEET_LOGS_SUBCOLLECTION),
            where('sheetId', '==', sheetId),
            orderBy('updatedAt', 'desc'),
            limit(safeLimit)
          ));
        } catch (error) {
          if (!isPermissionDeniedError(error)) {
            console.warn('Failed to load workspace activity logs for sheet:', sheetId, error);
          }
          return null;
        }
      })
    );

    return perSheetSnapshots
      .filter(Boolean)
      .flatMap((snapshot) => snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data()
      })))
      .sort((left, right) => {
        const leftTime = left.updatedAt?.toMillis?.()
          || left.createdAt?.toMillis?.()
          || new Date(left.updatedAt || left.createdAt || 0).getTime();
        const rightTime = right.updatedAt?.toMillis?.()
          || right.createdAt?.toMillis?.()
          || new Date(right.updatedAt || right.createdAt || 0).getTime();
        return rightTime - leftTime;
      })
      .slice(0, safeLimit)
      .map((activity) => {
        const sheet = sheetMap.get(activity.sheetId);
        return {
          ...activity,
          sheetTitle: `${sheet?.title || activity.sheetTitle || 'Untitled Sheet'}`.trim(),
          workspaceId: `${sheet?.workspaceId || activity.workspaceId || ''}`.trim(),
          workspaceTitle: `${sheet?.workspaceTitle || activity.workspaceTitle || ''}`.trim()
        };
      });
  },

  async getTemplateSheets(userId) {
    const currentUserId = userId || auth.currentUser?.uid;
    if (!currentUserId) {
      throw new Error('User is required to load sheet templates');
    }

    const snapshot = await getDocs(query(
      collection(db, SHEETS_COLLECTION),
      where('isTemplate', '==', true)
    ));

    return sortSheetsByUpdatedAtDesc(snapshot.docs.map((item) => ({
      id: item.id,
      ...item.data()
    })));
  },

  async getSheetById(sheetId) {
    const docSnap = await getDoc(doc(db, SHEETS_COLLECTION, sheetId));
    if (!docSnap.exists()) {
      throw new Error('Sheet not found');
    }
    const data = { id: docSnap.id, ...docSnap.data() };
    const currentUserId = auth.currentUser?.uid || null;

    if (currentUserId && data.userId === currentUserId) {
      return {
        ...data,
        sharedAccess: 'owner',
        ...(data.visibility === 'restricted'
          ? await loadSheetShares(sheetId)
          : {
              allowedEmails: [],
              allowedUserIds: [],
              sharedUsers: []
            })
      };
    }

    const currentUserShare = await loadCurrentUserSheetShare(sheetId);

    return {
      ...data,
      sharedAccess: currentUserShare?.access || data.collaboratorAccess || 'view',
      allowedEmails: [],
      allowedUserIds: [],
      sharedUsers: []
    };
  },

  subscribeToSheet(sheetId, onData, onError) {
    const sheetRef = doc(db, SHEETS_COLLECTION, sheetId);

    return onSnapshot(
      sheetRef,
      async (docSnap) => {
        if (!docSnap.exists()) {
          onError?.(new Error('Sheet not found'));
          return;
        }

        const data = { id: docSnap.id, ...docSnap.data() };
        const currentUserId = auth.currentUser?.uid || null;

        if (currentUserId && data.userId === currentUserId) {
          try {
            onData?.({
              ...data,
              sharedAccess: 'owner',
              ...(data.visibility === 'restricted'
                ? await loadSheetShares(sheetId)
                : {
                    allowedEmails: [],
                    allowedUserIds: [],
                    sharedUsers: []
                  })
            });
            return;
          } catch (error) {
            console.warn('Failed to load live sheet shares:', sheetId, error);
          }
        }

        const currentUserShare = await loadCurrentUserSheetShare(sheetId);

        onData?.({
          ...data,
          sharedAccess: currentUserShare?.access || data.collaboratorAccess || 'view',
          allowedEmails: [],
          allowedUserIds: [],
          sharedUsers: []
        });
      },
      (error) => {
        onError?.(error);
      }
    );
  },

  subscribeToSheetRows(sheetId, options = {}, onData, onError) {
    const safeLimit = Math.max(1, Math.min(500, Number(options?.maxResults) || 200));
    const providedSheet = options?.sheetData || null;
    let cancelled = false;
    let unsubscribeRoot = null;
    const contributorUnsubscribers = new Map();
    const contributorRows = new Map();
    let unsubscribeLegacy = null;

    const cleanupContributorListeners = () => {
      contributorUnsubscribers.forEach((unsubscribe) => unsubscribe?.());
      contributorUnsubscribers.clear();
      contributorRows.clear();
    };

    const cleanup = () => {
      cancelled = true;
      unsubscribeRoot?.();
      unsubscribeRoot = null;
      unsubscribeLegacy?.();
      unsubscribeLegacy = null;
      cleanupContributorListeners();
    };

    const start = async () => {
      try {
        const sheet = providedSheet || await this.getSheetById(sheetId);
        if (cancelled) return;

        if (!isOwnRowsMode(sheet)) {
          unsubscribeRoot = onSnapshot(
            query(
              collection(db, SHEETS_COLLECTION, sheetId, SHEET_ITEMS_SUBCOLLECTION),
              orderBy('createdAt', 'asc'),
              limit(safeLimit)
            ),
            (snapshot) => {
              onData?.(
                snapshot.docs.map((item) => ({
                  id: item.id,
                  storageMode: 'sheet-items',
                  ...item.data()
                }))
              );
            },
            (error) => {
              onError?.(error);
            }
          );
          return;
        }

        const currentUserId = auth.currentUser?.uid;
        if (!currentUserId) {
          onData?.([]);
          return;
        }

        const isOwner = sheet.userId === currentUserId;
        if (!isOwner) {
          unsubscribeRoot = onSnapshot(
            query(
              getSharedRowItemsCollection(sheetId, currentUserId),
              orderBy('createdAt', 'asc'),
              limit(safeLimit)
            ),
            (snapshot) => {
              onData?.(snapshot.docs.map((item) => normalizeSharedRowDoc(item, currentUserId)));
            },
            (error) => {
              onError?.(error);
            }
          );
          return;
        }

        let legacyRows = [];

        const emitOwnerRows = () => {
          const nextRows = sortRowsByCreatedAt([
            ...legacyRows,
            ...Array.from(contributorRows.values()).flat()
          ]).slice(0, safeLimit);
          onData?.(nextRows);
        };

        unsubscribeLegacy = onSnapshot(
          query(
            collection(db, SHEETS_COLLECTION, sheetId, SHEET_ITEMS_SUBCOLLECTION),
            orderBy('createdAt', 'asc'),
            limit(safeLimit)
          ),
          (snapshot) => {
            legacyRows = snapshot.docs.map((item) => ({
              id: item.id,
              storageMode: 'sheet-items',
              ...item.data()
            }));
            emitOwnerRows();
          },
          (error) => {
            onError?.(error);
          }
        );

        unsubscribeRoot = onSnapshot(
          query(
            collection(db, SHEETS_COLLECTION, sheetId, SHARED_ROW_USERS_SUBCOLLECTION),
            orderBy('updatedAt', 'asc'),
            limit(safeLimit)
          ),
          (contributorsSnapshot) => {
            const activeContributorIds = new Set(contributorsSnapshot.docs.map((contributorDoc) => contributorDoc.id));

            contributorUnsubscribers.forEach((unsubscribe, contributorId) => {
              if (!activeContributorIds.has(contributorId)) {
                unsubscribe?.();
                contributorUnsubscribers.delete(contributorId);
                contributorRows.delete(contributorId);
              }
            });

            contributorsSnapshot.docs.forEach((contributorDoc) => {
              if (contributorUnsubscribers.has(contributorDoc.id)) {
                return;
              }

              const unsubscribeContributor = onSnapshot(
                query(
                  getSharedRowItemsCollection(sheetId, contributorDoc.id),
                  orderBy('createdAt', 'asc'),
                  limit(safeLimit)
                ),
                (itemsSnapshot) => {
                  contributorRows.set(
                    contributorDoc.id,
                    itemsSnapshot.docs.map((item) => normalizeSharedRowDoc(item, contributorDoc.id))
                  );
                  emitOwnerRows();
                },
                (error) => {
                  onError?.(error);
                }
              );

              contributorUnsubscribers.set(contributorDoc.id, unsubscribeContributor);
            });

            emitOwnerRows();
          },
          (error) => {
            onError?.(error);
          }
        );
      } catch (error) {
        onError?.(error);
      }
    };

    start();
    return cleanup;
  },

  async updateSheet(sheetId, updates) {
    const nextUpdates = { ...updates };
    const existingSheet = await this.getSheetById(sheetId);
    const existingWorkspaceId = `${existingSheet.workspaceId || ''}`.trim();
    const hasWorkspaceUpdate = Object.prototype.hasOwnProperty.call(nextUpdates, 'workspaceId');
    const workspaceId = hasWorkspaceUpdate
      ? `${nextUpdates.workspaceId || ''}`.trim()
      : existingWorkspaceId;
    const workspaceIsTemplate = await getTemplateWorkspaceState(workspaceId);
    const normalizedUpdates = applyTemplateWorkspaceSheetDefaults(nextUpdates, workspaceIsTemplate);
    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'organizationIds')) {
      normalizedUpdates.organizationIds = normalizeOrganizationIds(normalizedUpdates.organizationIds);
    }
    const effectiveOrganizationIds = Object.prototype.hasOwnProperty.call(normalizedUpdates, 'organizationIds')
      ? normalizedUpdates.organizationIds
      : normalizeOrganizationIds(existingSheet.organizationIds);
    if (effectiveOrganizationIds.length) {
      normalizedUpdates.visibility = 'restricted';
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'visibility') || workspaceIsTemplate) {
      const visibility = sanitizeVisibility(normalizedUpdates.visibility ?? existingSheet.visibility);
      normalizedUpdates.visibility = visibility;
      normalizedUpdates.allowedEmails = visibility === 'restricted'
        ? normalizeEmails(normalizedUpdates.allowedEmails || [])
        : [];
      normalizedUpdates.allowedUserIds = visibility === 'restricted'
        ? normalizeUserIds(normalizedUpdates.allowedUserIds || [])
        : [];
      normalizedUpdates.collaboratorAccess = visibility === 'restricted'
        ? sanitizeCollaboratorAccess(normalizedUpdates.collaboratorAccess)
        : 'view';
    } else if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'allowedEmails')) {
      normalizedUpdates.allowedEmails = normalizeEmails(normalizedUpdates.allowedEmails || []);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'collaboratorAccess')) {
      normalizedUpdates.collaboratorAccess = sanitizeCollaboratorAccess(normalizedUpdates.collaboratorAccess);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'isTemplate')) {
      normalizedUpdates.isTemplate = sanitizeTemplateFlag(normalizedUpdates.isTemplate);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'isLogSheet')) {
      normalizedUpdates.isLogSheet = sanitizeLogSheetFlag(normalizedUpdates.isLogSheet);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'adminBuilderAccessEnabled')) {
      normalizedUpdates.adminBuilderAccessEnabled = sanitizeAdminBuilderAccessEnabled(normalizedUpdates.adminBuilderAccessEnabled);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'customLogSheetId')) {
      normalizedUpdates.customLogSheetId = `${normalizedUpdates.customLogSheetId || ''}`.trim();
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'title')) {
      normalizedUpdates.title = normalizeSheetTitle(normalizedUpdates.title);
      if (!normalizedUpdates.title) {
        throw new Error('Sheet title is required');
      }
      normalizedUpdates.titleKey = getSheetTitleKey(normalizedUpdates.title);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'headers')) {
      normalizedUpdates.headers = sanitizeHeaders(normalizedUpdates.headers);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'actionForms')) {
      normalizedUpdates.actionForms = sanitizeActionForms(normalizedUpdates.actionForms || []);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'renderPreset')) {
      normalizedUpdates.renderPreset = sanitizeSheetRenderPreset(normalizedUpdates.renderPreset);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'primaryColor')) {
      normalizedUpdates.primaryColor = normalizeSheetThemeColor(normalizedUpdates.primaryColor);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'secondaryColor')) {
      normalizedUpdates.secondaryColor = normalizeSheetThemeColor(normalizedUpdates.secondaryColor);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'renderFont')) {
      normalizedUpdates.renderFont = normalizeSheetRenderFont(normalizedUpdates.renderFont);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'ogImageUrl')) {
      normalizedUpdates.ogImageUrl = normalizeSheetOgImageUrl(normalizedUpdates.ogImageUrl);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'aiSkills')) {
      const effectiveHeaders = normalizedUpdates.headers || existingSheet.headers || [];
      normalizedUpdates.aiSkills = sanitizeAiSkills(normalizedUpdates.aiSkills || {}, effectiveHeaders);
    }

    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'aiAutomations')) {
      const effectiveHeaders = normalizedUpdates.headers || existingSheet.headers || [];
      normalizedUpdates.aiAutomations = sanitizeAiAutomations(normalizedUpdates.aiAutomations || [], effectiveHeaders);
    }

    if (
      Object.prototype.hasOwnProperty.call(normalizedUpdates, 'filterHeaderIds') ||
      Object.prototype.hasOwnProperty.call(normalizedUpdates, 'headers')
    ) {
      const effectiveHeaders = normalizedUpdates.headers || existingSheet.headers || [];
      normalizedUpdates.filterHeaderIds = sanitizeFilterHeaderIds(normalizedUpdates.filterHeaderIds || [], effectiveHeaders);
    }

    if (
      Object.prototype.hasOwnProperty.call(normalizedUpdates, 'renderPreset') ||
      Object.prototype.hasOwnProperty.call(normalizedUpdates, 'renderPresetConfig') ||
      Object.prototype.hasOwnProperty.call(normalizedUpdates, 'headers')
    ) {
      const effectiveHeaders = normalizedUpdates.headers || existingSheet.headers || [];
      const effectiveRenderPreset = Object.prototype.hasOwnProperty.call(normalizedUpdates, 'renderPreset')
        ? normalizedUpdates.renderPreset
        : sanitizeSheetRenderPreset(existingSheet.renderPreset);
      normalizedUpdates.renderPreset = sanitizeSheetRenderPreset(effectiveRenderPreset);
      normalizedUpdates.renderPresetConfig = sanitizeSheetRenderPresetConfig(
        normalizedUpdates.renderPreset,
        Object.prototype.hasOwnProperty.call(normalizedUpdates, 'renderPresetConfig')
          ? normalizedUpdates.renderPresetConfig
          : existingSheet.renderPresetConfig,
        effectiveHeaders
      );
    }

    if (
      normalizedUpdates.visibility === 'restricted' ||
      Object.prototype.hasOwnProperty.call(normalizedUpdates, 'allowedEmails') ||
      Object.prototype.hasOwnProperty.call(normalizedUpdates, 'allowedUserIds') ||
      workspaceIsTemplate
    ) {
      const effectiveVisibility = normalizedUpdates.visibility ?? existingSheet.visibility;
      if (effectiveVisibility === 'restricted') {
        const rawShareTargets = await resolveRestrictedShareTargets(
          normalizedUpdates.allowedEmails ?? existingSheet.allowedEmails ?? [],
          normalizedUpdates.allowedUserIds ?? existingSheet.allowedUserIds ?? []
        );
        const shareTargets = restrictShareTargetsToOrganizations(rawShareTargets, effectiveOrganizationIds);
        normalizedUpdates.allowedEmails = shareTargets.allowedEmails;
        normalizedUpdates.allowedUserIds = shareTargets.allowedUserIds;
        normalizedUpdates.sharedUsers = shareTargets.sharedUsers;
      } else {
        normalizedUpdates.allowedEmails = [];
        normalizedUpdates.allowedUserIds = [];
        normalizedUpdates.sharedUsers = [];
      }
    }
    const workspaceTitle = hasWorkspaceUpdate
      ? `${normalizedUpdates.workspaceTitle || ''}`.trim()
      : `${existingSheet.workspaceTitle || ''}`.trim();

    await validateWorkspaceScopedReferences(
      workspaceId,
      normalizedUpdates.headers || existingSheet.headers || [],
      Object.prototype.hasOwnProperty.call(normalizedUpdates, 'customLogSheetId')
        ? normalizedUpdates.customLogSheetId
        : `${existingSheet.customLogSheetId || ''}`.trim(),
      sheetId,
      Object.prototype.hasOwnProperty.call(normalizedUpdates, 'actionForms')
        ? normalizedUpdates.actionForms
        : existingSheet.actionForms || []
    );

    await updateDoc(doc(db, SHEETS_COLLECTION, sheetId), stripUndefinedDeep({
      ...Object.fromEntries(
        Object.entries(normalizedUpdates).filter(([key]) => !['allowedEmails', 'allowedUserIds', 'sharedUsers'].includes(key))
      ),
      workspaceId: hasWorkspaceUpdate ? (workspaceId || null) : undefined,
      workspaceTitle: hasWorkspaceUpdate ? (workspaceTitle || null) : undefined,
      updatedAt: serverTimestamp()
    }));

    if (hasWorkspaceUpdate && existingWorkspaceId !== workspaceId) {
      if (existingWorkspaceId) {
        await sheetWorkspaceService.updateWorkspace(existingWorkspaceId, {
          sheetCount: increment(-1)
        });
      }
      if (workspaceId) {
        await sheetWorkspaceService.updateWorkspace(workspaceId, {
          sheetCount: increment(1)
        });
      }
    }

    if ((normalizedUpdates.visibility ?? existingSheet.visibility) === 'restricted') {
      await syncSheetShares(
        sheetId,
        {
          allowedEmails: normalizedUpdates.allowedEmails || [],
          allowedUserIds: normalizedUpdates.allowedUserIds || [],
          sharedUsers: normalizedUpdates.sharedUsers || []
        },
        normalizedUpdates.collaboratorAccess || 'view',
        auth.currentUser?.uid || ''
      );
    } else {
      await clearSheetShares(sheetId);
    }

    if (workspaceIsTemplate) {
      const linkedForm = await sheetFormService.getSheetFormById(sheetId).catch(() => null);
      if (linkedForm?.id) {
        await updateDoc(doc(db, SHEET_FORMS_COLLECTION, sheetId), {
          linkedSheetVisibility: 'public',
          accessMode: 'public',
          updatedAt: serverTimestamp()
        });
      }
    }

    return normalizedUpdates;
  },

  async deleteSheet(sheetId) {
    const existingSheet = await this.getSheetById(sheetId).catch(() => null);
    await deleteDoc(doc(db, SHEETS_COLLECTION, sheetId));
    if (existingSheet?.workspaceId) {
      await sheetWorkspaceService.updateWorkspace(existingSheet.workspaceId, {
        sheetCount: increment(-1)
      });
    }
  },

  async cloneSheet(sheetId, userId, options = {}) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to clone a sheet');

    const sourceSheet = await this.getSheetById(sheetId);
    const canCloneSource = sourceSheet.userId === ownerId || sourceSheet.isTemplate || options?.allowAccessibleSource === true;
    if (!canCloneSource) {
      throw new Error('Only the sheet owner can clone this sheet unless it is published as a template or accessible cloning is explicitly enabled');
    }

    const clonedSheetId = doc(collection(db, SHEETS_COLLECTION)).id;
    const requestedTitle = `${options?.title || ''}`.trim();
    const clonedTitle = requestedTitle || `${sourceSheet.title || 'Untitled Sheet'} (Copy)`;
    const clonedHeaders = (sourceSheet.headers || []).map((header) => {
      const optionSource = normalizeSheetOptionSource(header?.optionSource);
      const conditionalOptions = (Array.isArray(header?.conditionalOptions) ? header.conditionalOptions : []).map((rule) => {
        const ruleOptionSource = normalizeSheetOptionSource(rule?.optionSource);
        return {
          ...rule,
          optionSource: ruleOptionSource?.sheetId === sheetId
            ? { ...ruleOptionSource, sheetId: clonedSheetId }
            : ruleOptionSource || undefined
        };
      });
      return {
        ...header,
        conditionalOptions: conditionalOptions.length ? conditionalOptions : undefined,
        optionSource: optionSource?.sheetId === sheetId
          ? { ...optionSource, sheetId: clonedSheetId }
          : optionSource || undefined
      };
    });

    const clonePayload = {
      documentId: clonedSheetId,
      forceCreate: true,
      workspaceId: `${options?.workspaceId || ''}`.trim(),
      workspaceTitle: `${options?.workspaceTitle || ''}`.trim(),
      workspaceOnly: options?.workspaceOnly === true,
      title: clonedTitle,
      description: sourceSheet.description || '',
      customLogSheetId: '',
      actionForms: options?.includeActions === false
        ? []
        : remapSingleSheetCloneActionForms(sourceSheet.actionForms || [], sheetId, clonedSheetId),
      primaryColor: sourceSheet.primaryColor || undefined,
      secondaryColor: sourceSheet.secondaryColor || undefined,
      renderFont: normalizeSheetRenderFont(sourceSheet.renderFont),
      ogImageUrl: sourceSheet.ogImageUrl || undefined,
      renderPreset: sourceSheet.renderPreset || 'default',
      renderPresetConfig: sourceSheet.renderPresetConfig || undefined,
      headers: clonedHeaders,
      filterHeaderIds: sourceSheet.filterHeaderIds || [],
      analytics: sourceSheet.analytics || [],
      charts: [],
      aiSkills: sourceSheet.aiSkills || {},
      aiAutomations: sourceSheet.aiAutomations || [],
      visibility: 'private',
      allowedEmails: [],
      collaboratorAccess: 'view',
      isTemplate: false,
      originalDocumentId: sourceSheet.originalDocumentId || sheetId,
      templateClonedFromId: sourceSheet.isTemplate ? sheetId : undefined,
      templateClonedFromTitle: sourceSheet.isTemplate ? sourceSheet.title || '' : undefined
    };

    const clonedSheet = await this.saveSheet(clonePayload, ownerId);

    try {
      await cloneLinkedSheetForm({
        sourceSheetId: sheetId,
        clonedSheet,
        clonedHeaders,
        ownerId,
        workspaceId: clonePayload.workspaceId
      });
    } catch (error) {
      // A user may be allowed to clone a template without being allowed to read
      // its internal linked-form document. The sheet itself has already been
      // created at this point, so do not report the entire clone as failed.
      if (!isPermissionDeniedError(error)) throw error;
      console.warn('Linked sheet form was not copied because it is not accessible:', error);
    }

    const shouldCloneRows = options?.includeRows === true || sourceSheet.visibility === 'public';
    if (shouldCloneRows) {
      const sourceRows = await this.getSheetRows(sheetId, 500);
      const clonableRows = sourceRows.filter((row) => row?.storageMode === 'sheet-items');

      if (clonableRows.length) {
        await commitRowsInBatches(
          clonedSheet.id,
          clonableRows,
          ownerId,
          auth.currentUser?.email || null
        );
      }
    }

    return clonedSheet;
  },

  async cloneWorkspace(workspaceId, userId, options = {}) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to clone a workspace');

    const sourceWorkspace = await sheetWorkspaceService.getWorkspaceById(workspaceId);
    const requestedTitle = `${options?.title || ''}`.trim();
    const clonedWorkspaceTitle = requestedTitle || `${sourceWorkspace.title || 'Untitled Workspace'} Copy`;
    const includeRows = options?.includeRows !== false;

    let sourceSheets = [];
    if (sourceWorkspace.isTemplate === true) {
      sourceSheets = await this.getSheetsByWorkspaceIds([workspaceId]).catch(() => []);
    } else {
      const accessibleSheets = sourceWorkspace.userId === ownerId
        ? (await this.getUserSheets(ownerId).catch(() => []))
        : (await this.getSharedSheets(ownerId).catch(() => []));
      sourceSheets = accessibleSheets.filter((sheet) => `${sheet?.workspaceId || ''}`.trim() === `${workspaceId || ''}`.trim());
    }
    sourceSheets = sortSheetsByUpdatedAtDesc(sourceSheets);

    const clonedWorkspace = await sheetWorkspaceService.createWorkspace({
      title: clonedWorkspaceTitle,
      description: `${sourceWorkspace.description || ''}`.trim(),
      visibility: 'private',
      defaultCollaboratorAccess: sourceWorkspace.defaultCollaboratorAccess || 'view',
      allowCrossSheetReferences: sourceWorkspace.allowCrossSheetReferences !== false,
      primaryColor: sourceWorkspace.primaryColor,
      aiConfig: sourceWorkspace.aiConfig || {},
      agentConfig: sourceWorkspace.agentConfig || {},
      templateClonedFromId: sourceWorkspace.isTemplate === true ? sourceWorkspace.id : undefined
    }, ownerId);

    const sourceLogoUrl = `${sourceWorkspace.logoUrl || ''}`.trim();
    if (sourceLogoUrl) {
      await sheetWorkspaceService.updateWorkspace(clonedWorkspace.id, {
        logoUrl: sourceLogoUrl
      });
      clonedWorkspace.logoUrl = sourceLogoUrl;
    }

    const workspaceSheetIds = new Set(sourceSheets.map((sheet) => `${sheet?.id || ''}`.trim()).filter(Boolean));
    const sheetIdMap = new Map();
    const clonedSheets = [];

    for (const sourceSheet of sourceSheets) {
      const clonedSheet = await this.cloneSheet(sourceSheet.id, ownerId, {
        title: `${sourceSheet.title || 'Untitled Sheet'}`.trim() || 'Untitled Sheet',
        workspaceId: clonedWorkspace.id,
        workspaceTitle: clonedWorkspace.title || '',
        workspaceOnly: true,
        includeRows,
        includeActions: false
      });

      sheetIdMap.set(sourceSheet.id, clonedSheet.id);
      clonedSheets.push({
        sourceSheet,
        clonedSheet
      });
    }

    for (const { sourceSheet, clonedSheet } of clonedSheets) {
      const nextHeaders = remapWorkspaceCloneHeaders(sourceSheet.headers || [], sheetIdMap, workspaceSheetIds);
      const sourceCustomLogSheetId = `${sourceSheet.customLogSheetId || ''}`.trim();
      const nextCustomLogSheetId = workspaceSheetIds.has(sourceCustomLogSheetId)
        ? (sheetIdMap.get(sourceCustomLogSheetId) || '')
        : sourceCustomLogSheetId;
      const nextActionForms = remapWorkspaceCloneActionForms(sourceSheet.actionForms || [], sheetIdMap, workspaceSheetIds);

      await this.updateSheet(clonedSheet.id, {
        headers: nextHeaders,
        customLogSheetId: nextCustomLogSheetId,
        actionForms: nextActionForms,
        workspaceTitle: clonedWorkspace.title || ''
      });

      await remapWorkspaceCloneLinkedForms({
        clonedSheetId: clonedSheet.id,
        sheetIdMap,
        workspaceSheetIds
      });
    }

    const sourceDefaultLogSheetId = `${sourceWorkspace.defaultLogSheetId || ''}`.trim();
    if (sourceDefaultLogSheetId && sheetIdMap.has(sourceDefaultLogSheetId)) {
      await sheetWorkspaceService.updateWorkspace(clonedWorkspace.id, {
        defaultLogSheetId: sheetIdMap.get(sourceDefaultLogSheetId)
      });
    }

    return {
      workspace: clonedWorkspace,
      sheets: clonedSheets.map(({ clonedSheet }) => clonedSheet),
      sheetIdMap: Object.fromEntries(sheetIdMap.entries())
    };
  },

  async saveSheetRow(sheetId, rowData = {}, userId) {
    const sheet = await this.getSheetById(sheetId);
    const createdBy = userId || auth.currentUser?.uid || null;
    const createdByEmail = auth.currentUser?.email || null;
    const values = sanitizeRowValues(rowData.values || {}, sheet.headers || []);

    if (isOwnRowsMode(sheet)) {
      if (!createdBy) {
        throw new Error('You must be signed in to add rows to this sheet.');
      }

      const userRef = getSharedRowUserRef(sheetId, createdBy);
      const userSnap = await getDoc(userRef);
      const batch = writeBatch(db);
      batch.set(userRef, stripUndefinedDeep({
        sheetId,
        userId: createdBy,
        email: createdByEmail,
        displayName: auth.currentUser?.displayName || '',
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp()
      }), { merge: true });
      if (!userSnap.exists()) {
        batch.set(userRef, { createdAt: serverTimestamp() }, { merge: true });
      }
      await batch.commit();

      const docRef = await addDoc(getSharedRowItemsCollection(sheetId, createdBy), stripUndefinedDeep({
        sheetId,
        values,
        createdBy,
        createdByEmail,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }));

      return {
        id: docRef.id,
        sheetId,
        values,
        createdBy,
        createdByEmail,
        contributorId: createdBy,
        storageMode: 'shared-own-rows',
        createdAt: new Date(),
        updatedAt: new Date()
      };
    }

    const docRef = await addDoc(collection(db, SHEETS_COLLECTION, sheetId, SHEET_ITEMS_SUBCOLLECTION), stripUndefinedDeep({
      sheetId,
      values,
      createdBy,
      createdByEmail,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }));

    return {
      id: docRef.id,
      sheetId,
      values,
      createdBy,
      createdByEmail,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  },

  async updateSheetRow(sheetId, rowId, rowData = {}, userId) {
    const sheet = await this.getSheetById(sheetId);
    const updatedBy = userId || auth.currentUser?.uid || null;
    const values = sanitizeRowValues(rowData.values || {}, sheet.headers || []);

    if (isOwnRowsMode(sheet) && rowData.storageMode !== 'sheet-items') {
      const contributorId = rowData.contributorId || updatedBy;
      if (!contributorId) {
        throw new Error('Unable to determine which shared row to update.');
      }

      await updateDoc(
        doc(db, SHEETS_COLLECTION, sheetId, SHARED_ROW_USERS_SUBCOLLECTION, contributorId, SHARED_ROW_ITEMS_SUBCOLLECTION, rowId),
        stripUndefinedDeep({
          values,
          updatedBy,
          updatedAt: serverTimestamp()
        })
      );

      return {
        id: rowId,
        sheetId,
        values,
        updatedBy,
        contributorId,
        storageMode: 'shared-own-rows',
        updatedAt: new Date()
      };
    }

    await updateDoc(doc(db, SHEETS_COLLECTION, sheetId, SHEET_ITEMS_SUBCOLLECTION, rowId), stripUndefinedDeep({
      values,
      updatedBy,
      updatedAt: serverTimestamp()
    }));

    return {
      id: rowId,
      sheetId,
      values,
      updatedBy,
      updatedAt: new Date()
    };
  },

  async deleteSheetRow(sheetId, rowId, rowData = {}) {
    const sheet = await this.getSheetById(sheetId);

    if (isOwnRowsMode(sheet) && rowData.storageMode !== 'sheet-items') {
      const contributorId = rowData.contributorId || auth.currentUser?.uid;
      if (!contributorId) {
        throw new Error('Unable to determine which shared row to delete.');
      }
      await deleteDoc(
        doc(db, SHEETS_COLLECTION, sheetId, SHARED_ROW_USERS_SUBCOLLECTION, contributorId, SHARED_ROW_ITEMS_SUBCOLLECTION, rowId)
      );
      return;
    }

    await deleteDoc(doc(db, SHEETS_COLLECTION, sheetId, SHEET_ITEMS_SUBCOLLECTION, rowId));
  },

  async getSheetRows(sheetId, maxResults = 200) {
    const safeLimit = Math.max(1, Math.min(500, Number(maxResults) || 200));
    const sheet = await this.getSheetById(sheetId);
    const currentUserId = auth.currentUser?.uid;
    const isTemplateReader = Boolean(sheet?.isTemplate && currentUserId && sheet.userId !== currentUserId);

    if (isOwnRowsMode(sheet) && !isTemplateReader) {
      if (!currentUserId) return [];
      const isOwner = sheet.userId === currentUserId;

      if (!isOwner) {
        const ownRowsSnapshot = await getDocs(query(
          getSharedRowItemsCollection(sheetId, currentUserId),
          orderBy('createdAt', 'asc'),
          limit(safeLimit)
        ));

        return ownRowsSnapshot.docs.map((item) => normalizeSharedRowDoc(item, currentUserId));
      }

      try {
        const contributorsSnapshot = await getDocs(query(
          collection(db, SHEETS_COLLECTION, sheetId, SHARED_ROW_USERS_SUBCOLLECTION),
          orderBy('updatedAt', 'asc'),
          limit(safeLimit)
        ));

        const rowSnapshots = await Promise.all(
          contributorsSnapshot.docs.map(async (contributorDoc) => {
            const itemsSnapshot = await getDocs(query(
              getSharedRowItemsCollection(sheetId, contributorDoc.id),
              orderBy('createdAt', 'asc'),
              limit(safeLimit)
            ));

            return itemsSnapshot.docs.map((item) => normalizeSharedRowDoc(item, contributorDoc.id));
          })
        );

        const legacySnapshot = await getDocs(query(
          collection(db, SHEETS_COLLECTION, sheetId, SHEET_ITEMS_SUBCOLLECTION),
          orderBy('createdAt', 'asc'),
          limit(safeLimit)
        ));

        return [...rowSnapshots.flat(), ...legacySnapshot.docs.map((item) => ({
          id: item.id,
          storageMode: 'sheet-items',
          ...item.data()
        }))]
          .flat()
          .sort((left, right) => {
            const leftTime = left.createdAt?.toMillis?.() || new Date(left.createdAt || 0).getTime();
            const rightTime = right.createdAt?.toMillis?.() || new Date(right.createdAt || 0).getTime();
            return leftTime - rightTime;
          })
          .slice(0, safeLimit);
      } catch (error) {
        const ownRowsSnapshot = await getDocs(query(
          getSharedRowItemsCollection(sheetId, currentUserId),
          orderBy('createdAt', 'asc'),
          limit(safeLimit)
        ));

        return ownRowsSnapshot.docs.map((item) => normalizeSharedRowDoc(item, currentUserId));
      }
    }

    const q = query(
      collection(db, SHEETS_COLLECTION, sheetId, SHEET_ITEMS_SUBCOLLECTION),
      orderBy('createdAt', 'asc'),
      limit(safeLimit)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => ({
      id: item.id,
      storageMode: 'sheet-items',
      ...item.data()
    }));
  },

  subscribeToSheetLogs(sheetId, options = {}, onData, onError) {
    const safeLimit = Math.max(1, Math.min(200, Number(options?.maxResults) || 100));

    if (options.scopeType === 'row' || options.rowData?.id || options.rowData?.rowId) {
      return onSnapshot(
        query(
          getRowLogsCollection(sheetId, options.rowData || { rowId: options.rowId, contributorId: options.contributorId, storageMode: options.storageMode }),
          orderBy('updatedAt', 'desc'),
          limit(safeLimit)
        ),
        (snapshot) => {
          onData?.(snapshot.docs.map((item) => ({
            id: item.id,
            ...item.data()
          })));
        },
        (error) => onError?.(error)
      );
    }

    return onSnapshot(
      query(
        getSheetLogsCollection(sheetId),
        orderBy('updatedAt', 'desc'),
        limit(safeLimit)
      ),
      (snapshot) => {
        let logs = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data()
        }));

        if (options.scopeType && options.scopeType !== 'row') {
          logs = logs.filter((item) => item.scopeType === options.scopeType);
        }
        if (options.columnId) {
          logs = logs.filter((item) => item.columnId === options.columnId);
        }

        onData?.(logs);
      },
      (error) => onError?.(error)
    );
  },

  async saveSheetLog(sheetId, logData = {}, userId) {
    const currentUserId = userId || auth.currentUser?.uid || null;
    if (!currentUserId) {
      throw new Error('You must be signed in to save a log.');
    }

    const payload = sanitizeSheetLog(logData, {
      sheetId,
      userId: currentUserId,
      userEmail: auth.currentUser?.email || null
    });

    const targetCollection = payload.scopeType === 'row'
      ? getRowLogsCollection(sheetId, logData)
      : getSheetLogsCollection(sheetId);

    const docRef = await addDoc(targetCollection, {
      ...payload,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return {
      id: docRef.id,
      ...payload,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  },

  subscribeToSheetAiThreads(sheetId, options = {}, onData, onError) {
    const safeLimit = Math.max(1, Math.min(100, Number(options?.maxResults) || 50));

    return onSnapshot(
      query(
        getRowAiThreadsCollection(sheetId, options.rowData || { rowId: options.rowId, contributorId: options.contributorId, storageMode: options.storageMode }),
        orderBy('updatedAt', 'desc'),
        limit(safeLimit)
      ),
      (snapshot) => {
        const threads = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data()
        }));

        onData?.(threads);
      },
      (error) => onError?.(error)
    );
  },

  subscribeToSheetAiMessages(sheetId, rowData, threadId, onData, onError) {
    if (!threadId) {
      onData?.([]);
      return () => {};
    }

    return onSnapshot(
      query(
        getRowAiMessagesCollection(sheetId, rowData, threadId),
        orderBy('sequence', 'asc')
      ),
      (snapshot) => {
        onData?.(snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data()
        })));
      },
      (error) => onError?.(error)
    );
  },

  async saveSheetAiThread(sheetId, threadData = {}, messages = [], userId) {
    const currentUserId = userId || auth.currentUser?.uid || null;
    if (!currentUserId) {
      throw new Error('You must be signed in to save a chat.');
    }

    const safeMessages = (Array.isArray(messages) ? messages : [])
      .map((item) => sanitizeSheetAiMessage(item, {
        sheetId,
        userId: currentUserId,
        userEmail: auth.currentUser?.email || null
      }))
      .filter(Boolean);

    const threadRef = doc(getRowAiThreadsCollection(sheetId, threadData));
    const title = `${threadData.title || safeMessages.find((item) => item.role === 'user')?.content || 'Saved sheet chat'}`
      .trim()
      .slice(0, 120);
    const preview = `${safeMessages[safeMessages.length - 1]?.content || ''}`.trim().slice(0, 240);

    const batch = writeBatch(db);
    batch.set(threadRef, stripUndefinedDeep({
      sheetId,
      rowId: threadData.rowId || null,
      rowLabel: threadData.rowLabel || '',
      title,
      createdBy: currentUserId,
      createdByEmail: auth.currentUser?.email || null,
      lastMessagePreview: preview,
      messageCount: safeMessages.length,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }));

    safeMessages.forEach((message, index) => {
      const messageRef = doc(getRowAiMessagesCollection(sheetId, threadData, threadRef.id));
      batch.set(messageRef, {
        ...message,
        threadId: threadRef.id,
        sequence: index,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();

    return {
      id: threadRef.id,
      title,
      rowId: threadData.rowId || null,
      rowLabel: threadData.rowLabel || '',
      messageCount: safeMessages.length
    };
  },

  async appendSheetAiMessage(sheetId, rowData = {}, threadId, message = {}, userId) {
    const currentUserId = userId || auth.currentUser?.uid || null;
    if (!currentUserId) {
      throw new Error('You must be signed in to continue a saved chat.');
    }
    if (!threadId) {
      throw new Error('A saved chat thread is required.');
    }

    const threadSnap = await getDoc(getRowAiThreadRef(sheetId, rowData, threadId));
    const nextSequence = Number(threadSnap.data()?.messageCount || 0);
    const payload = sanitizeSheetAiMessage(message, {
      sheetId,
      threadId,
      userId: currentUserId,
      userEmail: auth.currentUser?.email || null
    });

    const docRef = await addDoc(getRowAiMessagesCollection(sheetId, rowData, threadId), {
      ...payload,
      sequence: nextSequence,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    await updateDoc(getRowAiThreadRef(sheetId, rowData, threadId), stripUndefinedDeep({
      updatedAt: serverTimestamp(),
      lastMessagePreview: payload.content.slice(0, 240),
      messageCount: increment(1)
    }));

    return {
      id: docRef.id,
      ...payload,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  },

  async chatWithSheetData(payload = {}) {
    const callable = httpsCallable(functions, 'chatWithSheetData', { timeout: 180000 });
    const result = await callable(payload);
    return result.data;
  },

  async chatWithWorkspaceData(payload = {}) {
    const callable = httpsCallable(functions, 'chatWithWorkspaceData', { timeout: 180000 });
    const result = await callable(payload);
    return result.data;
  },

  subscribeToWorkspaceAiChats(workspaceId, onData, onError, userId) {
    const currentUserId = userId || auth.currentUser?.uid;
    if (!workspaceId || !currentUserId) {
      onData?.([]);
      return () => {};
    }

    return onSnapshot(
      query(
        getWorkspaceAiChatsCollection(workspaceId, currentUserId),
        orderBy('updatedAt', 'desc'),
        limit(100)
      ),
      (snapshot) => {
        onData?.(snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data()
        })));
      },
      (error) => onError?.(error)
    );
  },

  async saveWorkspaceAiChat(workspaceId, chat = {}, userId) {
    const currentUserId = userId || auth.currentUser?.uid;
    if (!currentUserId) {
      throw new Error('You must be signed in to save a workspace chat.');
    }
    if (!workspaceId) {
      throw new Error('A workspace is required to save this chat.');
    }

    const chatsCollection = getWorkspaceAiChatsCollection(workspaceId, currentUserId);
    const chatRef = chat.id ? doc(chatsCollection, chat.id) : doc(chatsCollection);
    const existing = await getDoc(chatRef);
    const messages = (Array.isArray(chat.messages) ? chat.messages : [])
      .map((message, index) => {
        const role = message?.role === 'assistant' ? 'assistant' : 'user';
        const content = `${message?.content || ''}`.trim();
        if (!content) return null;
        return stripUndefinedDeep({
          id: `${message?.id || `${role}-${index}`}`.slice(0, 160),
          role,
          content,
          sheetTitles: Array.isArray(message?.sheetTitles)
            ? message.sheetTitles.map((title) => `${title || ''}`.trim()).filter(Boolean).slice(0, 50)
            : undefined
        });
      })
      .filter(Boolean)
      .slice(-100);
    const firstQuestion = messages.find((message) => message.role === 'user')?.content || '';
    const title = `${chat.title || firstQuestion || 'New chat'}`.trim().slice(0, 80);
    const preview = `${messages[messages.length - 1]?.content || ''}`.trim().slice(0, 240);

    await setDoc(chatRef, stripUndefinedDeep({
      workspaceId,
      userId: currentUserId,
      activeAgentId: `${chat.activeAgentId || ''}`.trim() || undefined,
      title,
      messages,
      messageCount: messages.length,
      lastMessagePreview: preview,
      createdAt: existing.exists() ? existing.data().createdAt : serverTimestamp(),
      updatedAt: serverTimestamp()
    }));

    return {
      id: chatRef.id,
      title,
      messages,
      messageCount: messages.length
    };
  },

  async generateSheetBlueprint(payload = {}) {
    const callable = httpsCallable(functions, 'generateSheetBlueprint', { timeout: 180000 });
    const result = await callable(payload);
    return result.data;
  },

  async getSheetShareMetadata(payload = {}) {
    const callable = httpsCallable(functions, 'getSheetShareMetadata', { timeout: 30000 });
    const result = await callable(payload);
    return result.data;
  },

  async calculateSheetAnalytics(payload = {}) {
    const callable = httpsCallable(functions, 'calculateSheetAnalytics', { timeout: 300000 });
    const result = await callable(payload);
    return result.data;
  },

  async calculateWorkspaceAnalytics(payload = {}) {
    const callable = httpsCallable(functions, 'calculateWorkspaceAnalytics', { timeout: 540000 });
    const result = await callable(payload);
    return result.data;
  },

  async sendBulkSmsForSheet(payload = {}) {
    const callable = httpsCallable(functions, 'sendBulkSmsForSheet', { timeout: 120000 });
    const result = await callable(payload);
    return result.data;
  },

  async sendEmailForSheet(payload = {}) {
    const callable = httpsCallable(functions, 'sendEmailForSheet', { timeout: 120000 });
    const result = await callable(payload);
    return result.data;
  }
};
